import { counterfeitEdgeStyles } from '../utils/counterfeitEdge';
import { ledgerEdgeStyles } from '../utils/ledgerEdge';
import { dimTransient, clearTransient } from '../utils/transientVisibility';
import { VISIBILITY } from '../constants/visibility';
import { decideExpansionLayout, LAYOUT_EXPANSION_THROTTLE_MS } from '../utils/layoutGate';
import React, { useEffect, useRef, forwardRef, useImperativeHandle, useState, useCallback, useMemo } from 'react';
import cytoscape from 'cytoscape';
import '../utils/workerElkLayout';
import CytoscapeComponent from 'react-cytoscapejs';
import { logLayoutEvent } from '../utils/canvasDebug';

import { useSelectionStore } from '../stores/useSelectionStore';
import { usePendingLayoutStore } from '../stores/usePendingLayoutStore';
import { canvasController } from '../services/canvasController';
import { useTheme } from '../hooks/useTheme';
import { CanvasNodeIntent, CanvasEdgeIntent } from '../types/canvasIntent';
import { useFilterStore, type FilterState } from '../stores/useFilterStore';
import { formatCurrency, getUsdEquivalent } from '../utils/edgeConsolidation';
import { clusterContextLeaves, expandCluster, CLUSTER_AUTO_THRESHOLD_NODES } from '../utils/placementClusters';
import { displayEntityName, isMagicAddress, resolveStoredCanvasLabel } from '../utils/displayName';
import { CanvasGridBackground, getCanvasExportBackground } from './workbench/CanvasGridBackground';
import { CanvasZoomControls } from './workbench/CanvasZoomControls';
import { classifyCanvasHit, shouldIgnoreDuplicateContextMenu } from '../utils/canvasContextMenu';
import { cytoscapeSafeElements } from '../utils/cytoscapeSafeElements';
import { criticalEndpointBorderColor, stampAssurance } from '../utils/canvasAssurance';
import { buildAssuranceNodeStyles, getCertaintyBadgeUri, getNotAssessedBadgeUri } from '../utils/canvasAssuranceStyles';
import { diagramFace } from '../constants/diagramPalette';

function paletteStyle(key: string, dark: boolean): Record<string, string> {
    const face = diagramFace(key, dark);
    const style: Record<string, string> = {
        'background-color': face.background,
        'border-color': face.border,
    };
    if (face.dash) style['border-style'] = 'dashed';
    return style;
}

// ── §9j composite background — function-mapped flag badge ──────────────
//
// The base node selector's `background-image` is a function mapper that
// returns the corner flag URI from `getFlagUri(ele)` — one of three
// variants (REVIEW amber / AUTO red-outline / CONFIRMED solid red), or
// a 1×1 transparent placeholder when no flag tag is set.
//
// The §9g category left-strip used to live in slot 0 of an array
// bg-image, but was REMOVED 2026-05-03 — see the inline comment block
// near `getFlagUri` for why. Single-slot bg-image is much simpler now.
//
// When future §9e (chain glyph) or §9h (status pill) ship, this can
// re-grow into an array. Until then, single-image is the right shape.
//
// Risk SCORE alone no longer paints its own canvas badge — the backend
// triage chokepoint sets `auto_flagged` whenever risk_score >= 0.7
// (also for sanctioned, mixer_entity), and that tag drives the flag.
// Low/medium-risk addresses no longer get a coloured dot — they're not
// actionable enough to warrant a permanent canvas badge, and the
// risk-tier chip in NodeIdentityChips still surfaces them in the
// Inspector when needed.
//
// Flag SVG geometry (Step 9j.2, May 2026):
// Each variant is a 22×22 viewBox composing
//   1) an OUTER white ring (concentric circle) — gives the badge
//      separation against ANY node bg (white card, dark sanctioned card,
//      green-strip Exchange, etc.) without needing a filter
//   2) the coloured backplate circle inside the ring
//   3) the Lucide Flag glyph (24×24) translate-scaled to centre inside
// `vector-effect: non-scaling-stroke` keeps the glyph's stroke crisp
// regardless of the inner scale transform.
//
// IMPORTANT — explicit width/height attrs on the <svg>: Cytoscape
// rasterises bg-image SVGs once at the browser's chosen natural size,
// then drawImage scales that bitmap on every zoom. Without explicit
// pixel dims the natural size defaults to the viewBox (22×22) and the
// badge looks blurry/distorted at canvas zoom > 2x. Setting width=88
// height=88 forces a 4x bitmap — crisp up to canvas zoom 4-5x without
// per-frame re-rasterisation. (Also: NO `feDropShadow` filter — its
// region maths re-rasterise at intermediate zoom buckets and produced
// the "flag changes shape between zoom levels" bug seen in 9j.2 v1.)
//
// THREE STATES — escalation amber → red-outline → red-solid:
//   REVIEW    (flagged_for_review)  white circle, AMBER stroke,  hollow amber flag
//   AUTO      (auto_flagged)        white circle, RED stroke,    hollow red flag
//   CONFIRMED (manually_flagged)    SOLID RED circle,            white solid flag
//
// Lucide's flag path is the same one bank-analyser uses inside their
// React-Flow circle badge — chosen because it stays recognisable at
// 16-20px export. The vertical pole + flowing drape read as "flag" even
// at canvas zoom-out where a hand-tuned 16×16 path would smear.
//
// Positioning (Option B — static, zero per-frame cost):
// `background-position-x: 100%, background-position-y: 0%` anchors the
// badge fully INSIDE the top-right corner. The outer white ring gives
// the badge presence against any node bg.
const _FLAG_SVG = (
    circleFill: string,
    circleStroke: string,
    flagFill: string,
    flagStroke: string,
): string =>
    `data:image/svg+xml;utf8,${encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' width='88' height='88' viewBox='0 0 22 22'>` +
            // Outer white ring — separation against any node bg. Drawn
            // first so the inner backplate paints on top.
            `<circle cx='11' cy='11' r='10.5' fill='#ffffff'/>` +
            // Inner coloured backplate.
            `<circle cx='11' cy='11' r='9.2' fill='${circleFill}' stroke='${circleStroke}' stroke-width='1.4'/>` +
            // Lucide flag glyph, translate+scaled to centre on (11,11).
            `<g transform='translate(4.2 4.2) scale(0.57)' fill='${flagFill}' stroke='${flagStroke}' stroke-width='2.6' stroke-linecap='round' stroke-linejoin='round' vector-effect='non-scaling-stroke'>` +
            `<path d='M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z'/>` +
            `<line x1='4' x2='4' y1='22' y2='15'/>` +
            `</g></svg>`,
    )}`;

// flagged_for_review — officer parked it for later. Amber, hollow.
const FLAG_SVG_REVIEW    = _FLAG_SVG('#ffffff', '#f59e0b', 'none',    '#f59e0b');
// auto_flagged — backend triage stamped it (risk_score≥0.7 / sanctioned / mixer).
// Red outline, hollow — "system says risky, awaiting human verdict".
const FLAG_SVG_AUTO      = _FLAG_SVG('#ffffff', '#ef4444', 'none',    '#ef4444');
// manually_flagged — officer's verdict ("Mark Suspicious"). Solid red fill.
const FLAG_SVG_CONFIRMED = _FLAG_SVG('#ef4444', '#ffffff', '#ffffff', '#ffffff');

// 1×1 transparent placeholder for bg-image slot 0 when no flag fires.
// Cytoscape needs *something* in the slot for the property to apply.
const TRANSPARENT_PX = `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'/>`,
)}`;

// §9g category left-strip was REMOVED 2026-05-03. The strip SVG had no
// explicit pixel dims so the browser rasterised at viewBox-natural 4×100
// and Cytoscape upscaled the bitmap unevenly at different zoom levels —
// strip would render half-height at one zoom and full at another. Plus
// `background-clip: 'none'` let the rectangle bleed past the round-rect
// corners. Entity-type already surfaces in the Inspector identity chip;
// the strip wasn't pulling enough weight to justify the rendering
// glitches. SmartContract keeps its dashed-border override (separate
// selector below in the stylesheet).

// Drawn into bg-image. Three flag-bearing tags, three visual
// variants so officers can read the flag's *origin* at a glance:
//   auto_flagged       — backend triage chokepoint (high_risk / sanctioned / mixer_entity) → red outline
//   manually_flagged   — Inspector ActionsTab → Mark Suspicious (officer's verdict)        → solid red
//   flagged_for_review — right-click context-menu Flag (officer parked it for later)        → amber outline
// Cascade is loudest-wins: a node carrying both manually_flagged AND
// auto_flagged shows the CONFIRMED badge (officer's verdict outranks
// the system's auto-flag).
const getFlagUri = (ele: any): string => {
    const tags = ele.data('entity_tags');
    if (!Array.isArray(tags)) return TRANSPARENT_PX;
    let hasAuto = false;
    let hasReview = false;
    for (const t of tags) {
        if (typeof t !== 'string') continue;
        if (t === 'manually_flagged') return FLAG_SVG_CONFIRMED; // loudest — officer's verdict
        if (t === 'auto_flagged') hasAuto = true;
        else if (t === 'flagged_for_review') hasReview = true;
    }
    if (hasAuto) return FLAG_SVG_AUTO;
    if (hasReview) return FLAG_SVG_REVIEW;
    return TRANSPARENT_PX;
};

// C5/F4 — "report generated" corner badge (bg-image slot 1, bottom-right).
// Indigo disc + white document glyph, same 88px-explicit-dims / no-feDropShadow
// rules as the flag (see the slot-architecture comment block above). Painted
// whenever the node carries any report_types entry; otherwise the transparent
// placeholder so the slot stays empty.
const _REPORT_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='88' height='88' viewBox='0 0 22 22'>` +
        `<circle cx='11' cy='11' r='10.5' fill='#ffffff'/>` +
        `<circle cx='11' cy='11' r='9.2' fill='#6366f1' stroke='#4338ca' stroke-width='1.4'/>` +
        `<g transform='translate(5.1 4.7) scale(0.49)' fill='none' stroke='#ffffff' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round' vector-effect='non-scaling-stroke'>` +
        `<path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'/>` +
        `<polyline points='14 2 14 8 20 8'/>` +
        `<line x1='8' x2='15' y1='13' y2='13'/>` +
        `<line x1='8' x2='15' y1='17' y2='17'/>` +
        `</g></svg>`,
)}`;

const getReportBadgeUri = (ele: any): string => {
    const rt = ele.data('report_types');
    return Array.isArray(rt) && rt.length > 0 ? _REPORT_SVG : TRANSPARENT_PX;
};

// Slot 2: Issuer Freeze badge (top-left, 0% 0%).
// Confirmed Frozen: Solid red circle with white snowflake.
const _FREEZE_SVG_FROZEN = `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='88' height='88' viewBox='0 0 22 22'>` +
        `<circle cx='11' cy='11' r='10.5' fill='#ffffff'/>` +
        `<circle cx='11' cy='11' r='9.2' fill='#dc2626' stroke='#991b1b' stroke-width='1.4'/>` +
        `<g transform='translate(5 5) scale(0.55)' fill='none' stroke='#ffffff' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' vector-effect='non-scaling-stroke'>` +
        `<line x1='2' x2='20' y1='11' y2='11'/><line x1='11' x2='11' y1='2' y2='20'/>` +
        `<path d='m18 15-4-4 4-4'/><path d='m4 7 4 4-4 4'/><path d='m15 4-4 4-4-4'/><path d='m7 18 4-4 4 4'/>` +
        `</g></svg>`,
)}`;

// Freeze Target (candidate for issuer petition): Amber circle with white snowflake.
const _FREEZE_SVG_TARGET = `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='88' height='88' viewBox='0 0 22 22'>` +
        `<circle cx='11' cy='11' r='10.5' fill='#ffffff'/>` +
        `<circle cx='11' cy='11' r='9.2' fill='#ea580c' stroke='#c2410c' stroke-width='1.4'/>` +
        `<g transform='translate(5 5) scale(0.55)' fill='none' stroke='#ffffff' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' vector-effect='non-scaling-stroke'>` +
        `<line x1='2' x2='20' y1='11' y2='11'/><line x1='11' x2='11' y1='2' y2='20'/>` +
        `<path d='m18 15-4-4 4-4'/><path d='m4 7 4 4-4 4'/><path d='m15 4-4 4-4-4'/><path d='m7 18 4-4 4 4'/>` +
        `</g></svg>`,
)}`;

const getFreezeBadgeUri = (ele: any): string => {
    const d = ele.data();
    if (!d) return TRANSPARENT_PX;
    if (d.is_frozen === true || d.freeze_status?.is_frozen === true) {
        return _FREEZE_SVG_FROZEN;
    }
    if (
        d.issuer_freeze_recommended === true ||
        d.black_usdt === true ||
        d.freeze_status?.freeze_recommended === true ||
        (Array.isArray(d.labels) && (d.labels.includes('black_usdt') || d.labels.includes('freeze_target')))
    ) {
        return _FREEZE_SVG_TARGET;
    }
    return TRANSPARENT_PX;
};

// Slot 3: Genesis Funder / Originating Source badge (bottom-left, 0% 100%).
// Teal circle with white lightning bolt.
const _SOURCE_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='88' height='88' viewBox='0 0 22 22'>` +
        `<circle cx='11' cy='11' r='10.5' fill='#ffffff'/>` +
        `<circle cx='11' cy='11' r='9.2' fill='#0d9488' stroke='#0f766e' stroke-width='1.4'/>` +
        `<g transform='translate(4.8 4.6) scale(0.54)' fill='#ffffff' stroke='#ffffff' stroke-width='1' stroke-linecap='round' stroke-linejoin='round' vector-effect='non-scaling-stroke'>` +
        `<polygon points='13 2 3 14 12 14 11 22 21 10 12 10 13 2'/>` +
        `</g></svg>`,
)}`;

const getSourceBadgeUri = (ele: any): string => {
    const d = ele.data();
    if (!d) return TRANSPARENT_PX;
    if (
        d.placement_role === 'source' ||
        d.is_genesis_funder === true ||
        d.is_funding_source === true ||
        d.funder_address ||
        (Array.isArray(d.labels) && d.labels.includes('gas_funder'))
    ) {
        return _SOURCE_SVG;
    }
    return TRANSPARENT_PX;
};

export interface NodeHoverPosition {
    x: number;
    y: number;
    /** Viewport-coordinate bounding box of the hovered node — used to position flanking buttons */
    bbox?: { x1: number; y1: number; x2: number; y2: number };
}

interface GraphCanvasProps {
    elements: any[];
    onNodeClick?: (node: any) => void;
    onEdgeClick?: (edge: any) => void;
    onEdgeContextMenu?: (edge: any, position: { x: number; y: number }) => void;
    onEdgeSelectionChange?: (edgeIds: string[]) => void;
    onNodeSelectionChange?: (nodeIds: string[]) => void;
    onNodeHover?: (node: any | null, position: NodeHoverPosition) => void;
    onNodeContextMenu?: (node: any, position: { x: number; y: number }) => void;
    /** Called when user clicks the ← expand-incoming button on a hovered node */
    onExpandNodeIn?: (nodeId: string, nodeData: any) => void;
    /** Called when user clicks the → expand-outgoing button on a hovered node */
    onExpandNodeOut?: (nodeId: string, nodeData: any) => void;
    /** Fired on every pan, zoom, or node drag — parent should re-query bbox for pinned hover node */
    onViewportChange?: () => void;
    selectedNodeId?: string | null;
}

/** Layout modes the canvas tools strip offers ("Rearrange as"). */
export type RearrangeMode = 'smart' | 'flow' | 'organic' | 'radial' | 'circle' | 'grid';
/** A concrete layout ('smart' resolves to one of the ELK three). */
export type ResolvedLayoutMode = Exclude<RearrangeMode, 'smart'>;

export interface GraphCanvasRef {
    exportPng: () => void;
    getCy: () => cytoscape.Core | null;
    highlightEdge: (edgeId: string) => void;
    emphasizeEdge: (edgeId: string) => void;
    deemphasizeEdge: (edgeId: string) => void;
    clearEdgeEmphasis: (edgeId: string) => void;
    applyEdgeFocus: (edgeIds: string[]) => void;
    clearEdgeFocus: () => void;
    clearEdgeSelection: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
    fitToView: () => void;
    resetZoom: () => void;
    /** R-CANVAS-10: Unlock all manually positioned nodes so next layout re-positions them */
    unlockAllNodes: () => void;
    /**
     * Force a fresh ELK layout run on the current graph.
     *
     * When `selectedIds` is provided (2+ ids), only those nodes and the
     * edges between them are re-laid out; all other nodes are locked in
     * place so surrounding context is preserved. In subset mode the
     * rearranged cluster is translated back to its original center so it
     * doesn't jump over other clusters.
     *
     * `mode` selects the ELK algorithm:
     *   - 'smart': inspects graph shape (density, hubs, cycles, entity types)
     *              and picks the best of the three below
     *   - 'flow' (default): layered left→right — best for clean directional tx flow
     *   - 'organic': stress-majorization force layout — best for cyclic/dense graphs
     *   - 'radial': radial tree — best for hub-and-spoke patterns
     *   - 'circle': Cytoscape built-in circle — every node on one ring
     *   - 'grid': Cytoscape built-in grid — rows and columns
     *
     * Returns info about what ran (useful for "Smart" mode UI feedback).
     */
    rearrangeLayout: (
        selectedIds?: Set<string>,
        mode?: RearrangeMode,
    ) => { mode: ResolvedLayoutMode; reason?: string } | void;
    /** R-CANVAS-03: Toggle "Follow the Money" mode — dim non-gold-path elements */
    toggleFollowMoney: (active: boolean) => void;
    /** Lock/unlock a specific node as manually positioned so layouts respect pin state */
    setNodeManualLock: (nodeId: string, locked: boolean) => void;
    /** Bidirectional sync: highlight edges matching tx hashes + zoom/center */
    highlightTxHashes: (hashes: Set<string>) => void;
    /** Bidirectional sync: clear tx selection highlights */
    clearTxHighlight: () => void;
}

// ─── Step 3 of CANVAS_PLACEMENT_LANGUAGE — within-column sub-axis ────────────
//
// After ELK's layered (flow) layout assigns nodes to columns, walk each column
// with ≥4 nodes and re-stack their y-coordinates so the column reads top-to-
// bottom by a meaningful secondary signal:
//
//   sources / sinks       →  edge-USD-value-sum descending (heaviest at top)
//   relays / shared_relay →  attribution confidence descending (known at top)
//   context / others      →  preserved in current y-order
//
// The subject column is left alone — Step 1+6 anchors the subject and the
// 1-hop fit centres on it. Locked nodes (user-pinned) are also excluded.
//
// Why post-pass rather than ELK config: ELK's BRANDES_KOEPF placement
// optimises for edge-crossing minimisation, which directly conflicts with
// our "magnitude funnel" requirement. Letting ELK lay out first, then
// re-stacking inside each column, gives us the legibility win while
// preserving ELK's column-assignment (which is the part it does well).
//
// References:
//   india-le-platform/docs/CANVAS_PLACEMENT_LANGUAGE.md §3.3, §12.2
//   india-le-platform/docs/CANVAS_PLACEMENT_STUDIES.md §3 (sample B)
const SUB_AXIS_MIN_COLUMN_NODES = 4;
const SUB_AXIS_COLUMN_TOLERANCE_PX = 80;
const SUB_AXIS_ANIM_MS = 280;

// B3 — minimum gap between two animated ELK layouts during an expansion.
// SSE / auto-investigation pushes a new `elements` set every ~200ms; without a
// floor here the canvas re-runs a full animated layout per batch and "slot-
// machines". 600ms > the 460ms layout settle, so a throttled run never pre-empts
// a still-animating one. Initial load and node removals are never throttled.

function applyWithinColumnSubAxis(cy: any): { columns: number; reordered: number } {
    if (!cy || cy.nodes().length < SUB_AXIS_MIN_COLUMN_NODES) {
        return { columns: 0, reordered: 0 };
    }

    // 1. Bucket nodes by x — same column = same x bucket (within tolerance).
    //    Subject nodes and locked (user-pinned) nodes are excluded entirely.
    const buckets = new Map<number, any[]>();
    cy.nodes().forEach((n: any) => {
        if (n.locked()) return;
        if (n.data('placement_role') === 'subject') return;
        const x = n.position().x;
        const bucketX = Math.round(x / SUB_AXIS_COLUMN_TOLERANCE_PX) * SUB_AXIS_COLUMN_TOLERANCE_PX;
        if (!buckets.has(bucketX)) buckets.set(bucketX, []);
        (buckets.get(bucketX) as any[]).push(n);
    });

    let columnsReordered = 0;
    let totalReordered = 0;

    buckets.forEach((column) => {
        if (column.length < SUB_AXIS_MIN_COLUMN_NODES) return;

        // 2. Compute a rank per node. Lower rank → smaller y (top of column).
        const ranked = column.map((n: any) => {
            const role = String(n.data('placement_role') || 'context');
            const originalY = Number(n.position().y);
            let rank: number;

            if (role === 'source' || role === 'sink') {
                // Sum USD-equivalent value across all edges touching this node.
                // Heaviest movers at top → most negative rank.
                let valueSum = 0;
                n.connectedEdges().forEach((e: any) => {
                    const d = e.data();
                    const raw = d.value_usd ?? d.total_value ?? d.value ?? 0;
                    const token = d.token_symbol ?? d.token;
                    valueSum += getUsdEquivalent(Number(raw), token);
                });
                rank = -valueSum;
            } else if (role === 'relay' || role === 'shared_relay') {
                const confidence = Number(
                    n.data('confidence') ??
                    n.data('entity_confidence') ??
                    n.data('attribution_confidence') ??
                    0
                );
                rank = -confidence;
            } else {
                // context / ghost / unranked — keep current relative y order
                rank = originalY;
            }

            return { node: n, rank, originalY };
        });

        // 3. Sort ascending by rank. Stable for ties.
        ranked.sort((a, b) => a.rank - b.rank);

        // 4. Re-stack evenly across the column's existing y span. Preserving
        //    the span (not the bbox) means ELK's column-height is respected;
        //    we only change *order* within it.
        const ys = column.map((n: any) => Number(n.position().y));
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const yRange = maxY - minY;
        if (yRange < 10) return; // column is already collapsed — nothing to gain
        const step = yRange / (column.length - 1);

        // 5. Animate each moved node to its new y. Only animate moves >2px so
        //    minor float drift doesn't trigger needless transitions.
        let anyMoved = false;
        ranked.forEach((r, i) => {
            const targetY = minY + step * i;
            if (Math.abs(r.originalY - targetY) > 2) {
                anyMoved = true;
                totalReordered++;
                r.node.animate(
                    { position: { x: r.node.position().x, y: targetY } },
                    { duration: SUB_AXIS_ANIM_MS, easing: 'ease-out-cubic' as any }
                );
            }
        });
        if (anyMoved) columnsReordered++;
    });

    return { columns: columnsReordered, reordered: totalReordered };
}

export const GraphCanvasImpl = forwardRef<GraphCanvasRef, GraphCanvasProps>(({ elements, onNodeClick, onEdgeClick, onEdgeContextMenu, onEdgeSelectionChange, onNodeSelectionChange, onNodeHover, onNodeContextMenu, onExpandNodeIn, onExpandNodeOut, onViewportChange, selectedNodeId = null }, ref) => {
    const theme = useTheme();
    const safeElements = useMemo(() => stampAssurance(cytoscapeSafeElements(elements || [])), [elements]);
    const isDark = theme === 'dark';
    const cyRef = useRef<cytoscape.Core | null>(null);
    const [zoomLevel, setZoomLevel] = useState(100);
    const [multiSelectedEdgeIds, setMultiSelectedEdgeIds] = useState<string[]>([]);

    // Stable callback refs — event listeners reference these instead of being re-bound
    const onNodeClickRef = useRef(onNodeClick);
    const onEdgeClickRef = useRef(onEdgeClick);
    const onEdgeContextMenuRef = useRef(onEdgeContextMenu);
    const onEdgeSelectionChangeRef = useRef(onEdgeSelectionChange);
    const onNodeSelectionChangeRef = useRef(onNodeSelectionChange);
    const onNodeHoverRef = useRef(onNodeHover);
    const onNodeContextMenuRef = useRef(onNodeContextMenu);
    const onExpandNodeInRef = useRef(onExpandNodeIn);
    const onExpandNodeOutRef = useRef(onExpandNodeOut);
    const onViewportChangeRef = useRef(onViewportChange);
    const selectedNodeIdRef = useRef(selectedNodeId);
    const multiSelectedEdgeIdsRef = useRef(multiSelectedEdgeIds);

    // R-CANVAS-10: Track manually positioned (dragged) node IDs — these stay locked during layout re-runs
    const manuallyPositionedNodeIdsRef = useRef<Set<string>>(new Set());

    // Keep refs up to date on every render (no re-binding needed)
    onNodeClickRef.current = onNodeClick;
    onEdgeClickRef.current = onEdgeClick;
    onEdgeContextMenuRef.current = onEdgeContextMenu;
    onEdgeSelectionChangeRef.current = onEdgeSelectionChange;
    onNodeSelectionChangeRef.current = onNodeSelectionChange;
    onNodeHoverRef.current = onNodeHover;
    onNodeContextMenuRef.current = onNodeContextMenu;
    onExpandNodeInRef.current = onExpandNodeIn;
    onExpandNodeOutRef.current = onExpandNodeOut;
    onViewportChangeRef.current = onViewportChange;
    selectedNodeIdRef.current = selectedNodeId;
    multiSelectedEdgeIdsRef.current = multiSelectedEdgeIds;

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        exportPng: () => {
            const cy = cyRef.current;
            if (cy) {
                const png = cy.png({
                    output: 'blob',
                    scale: 2,
                    bg: getCanvasExportBackground(),
                    full: true
                });
                const url = URL.createObjectURL(png as Blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `graph-export-${new Date().toISOString().slice(0, 10)}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        },
        getCy: () => cyRef.current,
        highlightEdge: (edgeId: string) => {
            const cy = cyRef.current;
            if (cy) {
                // Reset all edges first
                cy.edges().removeClass('edge-animated');

                // Highlight the specific edge
                const edge = cy.getElementById(edgeId);
                if (edge) {
                    edge.addClass('edge-animated');
                }
            }
        },
        emphasizeEdge: (edgeId: string) => {
            const cy = cyRef.current;
            if (cy) {
                const edge = cy.getElementById(edgeId);
                if (edge) {
                    edge.removeClass('edge-deemphasized').addClass('edge-emphasized');
                    clearTransient(edge, 'deemphasis');
                }
            }
        },
        deemphasizeEdge: (edgeId: string) => {
            const cy = cyRef.current;
            if (cy) {
                const edge = cy.getElementById(edgeId);
                if (edge) {
                    edge.removeClass('edge-emphasized').addClass('edge-deemphasized');
                    dimTransient(edge, VISIBILITY.PRUNED, 'deemphasis');
                }
            }
        },
        clearEdgeEmphasis: (edgeId: string) => {
            const cy = cyRef.current;
            if (cy) {
                const edge = cy.getElementById(edgeId);
                if (edge) {
                    edge.removeClass('edge-emphasized').removeClass('edge-deemphasized');
                    clearTransient(edge, 'deemphasis');
                }
            }
        },
        applyEdgeFocus: (edgeIds: string[]) => {
            const cy = cyRef.current;
            if (cy) {
                const focusSet = new Set(edgeIds);
                cy.edges().forEach((edge: any) => {
                    if (focusSet.has(edge.id())) {
                        edge.removeClass('edge-unfocused').addClass('edge-focused');
                        clearTransient(edge, 'focus');
                    } else {
                        edge.removeClass('edge-focused').addClass('edge-unfocused');
                        dimTransient(edge, VISIBILITY.PRUNED, 'focus');
                    }
                });
            }
        },
        clearEdgeFocus: () => {
            const cy = cyRef.current;
            if (cy) {
                cy.edges().removeClass('edge-focused').removeClass('edge-unfocused');
                clearTransient(cy.edges(), 'focus');
            }
        },
        clearEdgeSelection: () => {
            setMultiSelectedEdgeIds([]);
        },
        zoomIn: () => {
            const cy = cyRef.current;
            if (cy) {
                cy.zoom({ level: cy.zoom() * 1.3, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
            }
        },
        zoomOut: () => {
            const cy = cyRef.current;
            if (cy) {
                cy.zoom({ level: cy.zoom() / 1.3, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
            }
        },
        fitToView: () => {
            const cy = cyRef.current;
            if (cy) {
                cy.fit(undefined, 40);
                // Clamp zoom between 50% min and 175% max
                if (cy.zoom() > 1.75) cy.zoom(1.75);
                else if (cy.zoom() < 0.5) cy.zoom(0.5);
                cy.center();
            }
        },
        resetZoom: () => {
            const cy = cyRef.current;
            if (cy) {
                cy.zoom(1);
                cy.center();
            }
        },
        unlockAllNodes: () => {
            const cy = cyRef.current;
            if (cy) {
                manuallyPositionedNodeIdsRef.current.clear();
                cy.nodes().unlock().removeClass('manually-positioned');
            }
        },
        rearrangeLayout: (
            selectedIds?: Set<string>,
            requestedMode: RearrangeMode = 'flow',
        ) => {
            const cy = cyRef.current;
            if (!cy || cy.nodes().length === 0) {
                console.warn('[GraphCanvas] rearrangeLayout skipped: no cytoscape or empty canvas',
                    { hasCy: !!cy, nodeCount: cy?.nodes().length ?? 0, requestedMode });
                return;
            }
            // User-initiated: supersede any in-progress layout (don't silently no-op).
            // The prior auto-layout run from node-add effect may still be settling.
            if (layoutRunningRef.current) {
                console.info('[GraphCanvas] rearrangeLayout pre-empting in-progress layout',
                    { requestedMode });
                if (layoutSettleTimerRef.current) {
                    clearTimeout(layoutSettleTimerRef.current);
                    layoutSettleTimerRef.current = null;
                }
            }
            layoutRunningRef.current = true;

            const isSubsetMode = !!(selectedIds && selectedIds.size >= 2);

            // Build the element collection the layout should operate on.
            let layoutEles: any = cy.elements();
            let neighborhood: any = null;
            let preservedLocks: any = null;
            // Captured BEFORE layout runs — used to translate the subset
            // back to where it was so it doesn't land on top of another cluster.
            let originalCenter: { x: number; y: number } | null = null;

            if (isSubsetMode) {
                // Subset mode: resolve the selected nodes, include edges
                // that run between them, and lock everything else so it
                // keeps its current position.
                const selectedNodes = cy.nodes().filter((n: any) => selectedIds!.has(n.id()));
                if (selectedNodes.length < 2) {
                    layoutRunningRef.current = false;
                    return;
                }
                const internalEdges = selectedNodes.edgesWith(selectedNodes);
                neighborhood = selectedNodes.union(internalEdges);
                layoutEles = neighborhood;

                // Capture pre-layout center of the selected cluster.
                const bb = selectedNodes.boundingBox({ includeLabels: false, includeOverlays: false } as any);
                originalCenter = { x: (bb.x1 + bb.x2) / 2, y: (bb.y1 + bb.y2) / 2 };

                // Remember what was already locked so we can restore it;
                // then lock every node that is NOT part of the subset.
                preservedLocks = cy.nodes().filter((n: any) => n.locked());
                const toLock = cy.nodes().not(selectedNodes);
                toLock.lock();
                // Within the subset, unlock so the layout has full freedom —
                // EXCEPT for user-pinned nodes. B2 fix (May 4 2026 audit):
                // a pin is a sticky user preference. If the user explicitly
                // included a pinned node in a subset rearrange, the pin
                // still wins; ELK works around it. Without this guard the
                // subset code path silently destroyed pin state on every
                // Rearrange of a region that happened to include a pinned
                // node — which is what the user reported as "pin doesn't
                // hold under load."
                selectedNodes.forEach((n: any) => {
                    if (manuallyPositionedNodeIdsRef.current.has(n.id())) return;
                    n.unlock();
                });
            } else {
                // Full-graph mode. B2 fix: do NOT wipe the manual-pin set.
                // Pinning is a sticky preference — Rearrange should re-lay
                // out everything around the user's pinned anchors, not
                // discard them. We unlock all transient locks (e.g. from
                // a prior settle that may have left ghost lock state) but
                // PRESERVE manualIds, then re-apply locks for pinned nodes
                // below before ELK runs. The settle-time `cy.nodes().unlock()`
                // at the bottom of the auto-layout useEffect already keeps
                // pinned nodes draggable BETWEEN layouts; manualIds is what
                // tells the next layout "respect this position again."
                cy.nodes().unlock();
            }

            // Re-apply locks for user-pinned nodes so ELK respects them.
            // Mirrors the pattern in the node-add useEffect (~line 2800).
            const pinnedDuringLayout = manuallyPositionedNodeIdsRef.current;
            if (pinnedDuringLayout.size > 0) {
                cy.nodes().forEach((node: any) => {
                    if (pinnedDuringLayout.has(node.id())) {
                        node.lock();
                        node.addClass('manually-positioned');
                    }
                });
            }

            // Resolve 'smart' to a concrete algorithm by inspecting the
            // element collection we're about to lay out. For explicit modes,
            // keep the user's choice; only 'smart' triggers auto-selection.
            let mode: ResolvedLayoutMode;
            let smartReason: string | undefined;
            if (requestedMode === 'smart') {
                const analysis = analyzeGraph(layoutEles);
                mode = analysis.mode;
                smartReason = analysis.stats?.reason;
            } else {
                mode = requestedMode;
            }

            // Remember the user's choice so subsequent expansions / re-layouts
            // honour it. Only persist whole-graph rearranges; subset mode is a
            // local touch-up and shouldn't reshape future expansions. The
            // requestedMode (not the resolved mode) is what we store: smart is
            // a strategy, not a fixed algorithm — it should keep re-evaluating
            // on later expansions if the user picked it once.
            if (!isSubsetMode) {
                lastUserLayoutModeRef.current = requestedMode;
            }

            logLayoutEvent('layout_rearrange', {
                mode,
                requested: requestedMode,
                smartReason,
                subset: isSubsetMode,
                nodeCount: isSubsetMode ? (neighborhood?.nodes().length ?? 0) : cy.nodes().length,
                edgeCount: isSubsetMode ? (neighborhood?.edges().length ?? 0) : cy.edges().length,
                totalNodes: cy.nodes().length,
            });

            // In subset mode we DON'T want the layout to auto-fit the viewport —
            // we'll translate the result back to the cluster's original center
            // and let the caller decide whether to animate a fit.
            const config: any = { ...getLayoutConfig(mode), eles: layoutEles };
            if (isSubsetMode) {
                config.fit = false;
            }
            const layoutInstance = cy.layout(config);
            try {
                layoutInstance.run();
            } catch (e) {
                console.error('[GraphCanvas] ELK rearrangeLayout failed, falling back to cose', e);
                cy.layout({
                    name: 'cose',
                    animate: true,
                    animationDuration: 400,
                    fit: !isSubsetMode,
                    padding: 40,
                    randomize: false,
                    nodeOverlap: 20,
                } as any).run();
            }

            // Mirror the node-add useEffect's settle behavior.
            if (layoutSettleTimerRef.current) clearTimeout(layoutSettleTimerRef.current);
            layoutSettleTimerRef.current = setTimeout(() => {
                layoutSettleTimerRef.current = null;
                // Use-after-destroy guard — this timer can fire after the cy
                // instance is destroyed on unmount (renderer = null), and the
                // overlap/geometry reads below would throw on isHeadless.
                if (cy.destroyed()) return;

                // Post-layout overlap guard. Stress especially can land nodes
                // closer than comfortable when connectivity is dense. We run
                // a quick few passes pushing any overlapping pairs apart.
                // Only for organic/radial — flow's layered algorithm already
                // enforces layer spacing strictly.
                if (mode === 'organic' || mode === 'radial') {
                    const targetNodes = isSubsetMode && neighborhood
                        ? neighborhood.nodes()
                        : cy.nodes();
                    const MIN_GAP = 90; // px — must exceed a typical node diameter
                    for (let pass = 0; pass < 4; pass++) {
                        let shifted = false;
                        targetNodes.forEach((a: any) => {
                            if (a.locked()) return;
                            const ap = a.position();
                            const ar = (a.width() + a.height()) / 4 + MIN_GAP / 2;
                            targetNodes.forEach((b: any) => {
                                if (a.id() >= b.id()) return; // each pair once
                                const bp = b.position();
                                const br = (b.width() + b.height()) / 4 + MIN_GAP / 2;
                                const dx = bp.x - ap.x;
                                const dy = bp.y - ap.y;
                                const dist = Math.hypot(dx, dy) || 0.01;
                                const need = ar + br;
                                if (dist < need) {
                                    // Push each node half the shortfall along the axis between them.
                                    const push = (need - dist) / 2;
                                    const ux = dx / dist;
                                    const uy = dy / dist;
                                    if (!a.locked()) a.position({ x: ap.x - ux * push, y: ap.y - uy * push });
                                    if (!b.locked()) b.position({ x: bp.x + ux * push, y: bp.y + uy * push });
                                    shifted = true;
                                }
                            });
                        });
                        if (!shifted) break; // converged early
                    }
                }

                if (isSubsetMode && neighborhood && originalCenter) {
                    // ELK will have centered the subset at the origin (or wherever it picks).
                    // Translate every selected node by the delta between its new cluster
                    // center and the cluster's original center so it snaps back.
                    const selectedOnly = neighborhood.nodes();
                    const newBB = selectedOnly.boundingBox({ includeLabels: false, includeOverlays: false } as any);
                    const newCenter = { x: (newBB.x1 + newBB.x2) / 2, y: (newBB.y1 + newBB.y2) / 2 };
                    const dx = originalCenter.x - newCenter.x;
                    const dy = originalCenter.y - newCenter.y;
                    if (dx !== 0 || dy !== 0) {
                        selectedOnly.forEach((n: any) => {
                            const p = n.position();
                            n.position({ x: p.x + dx, y: p.y + dy });
                        });
                    }
                }

                if (isSubsetMode && preservedLocks) {
                    // Restore prior lock state: unlock everything first,
                    // then re-lock only those that were genuinely pinned
                    // by the user before this rearrange started.
                    cy.nodes().unlock();
                    preservedLocks.lock();
                }

                if (isSubsetMode) {
                    // Subset: don't reframe the whole canvas; the cluster is
                    // now back in its original position so the user's mental
                    // map is preserved.
                } else {
                    cy.fit(undefined, 40);
                    if (cy.zoom() > 1.75) cy.zoom(1.75);
                    if (cy.zoom() < 0.5) cy.zoom(0.5);
                }
                layoutRunningRef.current = false;
            }, 600);
            // Synchronous return so callers know which algorithm was chosen
            // (useful for Smart mode, where the caller wants to toast the reason).
            return { mode, reason: smartReason };
        },
        toggleFollowMoney: (active: boolean) => {
            const cy = cyRef.current;
            if (!cy) return;
            // v5.3: route through canvasController so the store's elements[]
            // stays in sync with Cytoscape's opacity. Previously we wrote to
            // cy.data() directly, which could drift on re-render.
            const goldEdges = cy.edges('.gold-path, [edgeType="gold-path"]');
            const goldNodes = goldEdges.connectedNodes();
            const goldAll = goldEdges.union(goldNodes);
            const goldIds = new Set<string>();
            goldAll.forEach((ele: any) => { const id = ele.id(); if (id) goldIds.add(id); });

            if (active) {
                // Dim everything NOT on the gold path
                const toDim: string[] = [];
                cy.elements().forEach((ele: any) => {
                    const id = ele.id();
                    if (!id) return;
                    if (goldIds.has(id)) return;
                    toDim.push(id);
                });
                if (toDim.length > 0) canvasController.dim(toDim, undefined, 'follow_money_on');
                cy.batch(() => { goldNodes.addClass('follow-money-highlight'); });
            } else {
                // Lift all dims on non-gold elements
                const toUndim: string[] = [];
                cy.elements().forEach((ele: any) => {
                    const id = ele.id();
                    if (!id) return;
                    if (goldIds.has(id)) return;
                    toUndim.push(id);
                });
                if (toUndim.length > 0) canvasController.undim(toUndim, 'follow_money_off');
                cy.batch(() => { cy.elements().removeClass('follow-money-highlight'); });
            }
        },
        setNodeManualLock: (nodeId: string, locked: boolean) => {
            const cy = cyRef.current;
            if (!cy || !nodeId) return;
            const node = cy.getElementById(nodeId);
            if (!node || node.length === 0) return;

            if (locked) {
                manuallyPositionedNodeIdsRef.current.add(nodeId);
                node.lock();
                node.addClass('manually-positioned');
            } else {
                manuallyPositionedNodeIdsRef.current.delete(nodeId);
                node.unlock();
                node.removeClass('manually-positioned');
            }
        },
        highlightTxHashes: (hashes: Set<string>) => {
            const cy = cyRef.current;
            if (!cy || hashes.size === 0) return;
            cy.batch(() => {
                cy.edges().removeClass('tx-selected');
                cy.edges().forEach(edge => {
                    const txHash = edge.data('tx_hash');
                    const traceTxs: any[] = edge.data('trace_transactions') || [];
                    const allTxs: any[] = edge.data('all_transactions') || [];
                    const match = (txHash && hashes.has(txHash)) ||
                        traceTxs.some((t: any) => t.tx_hash && hashes.has(t.tx_hash)) ||
                        allTxs.some((t: any) => t.tx_hash && hashes.has(t.tx_hash));
                    if (match) edge.addClass('tx-selected');
                });
            });
            const selected = cy.edges('.tx-selected');
            if (selected.length > 0) {
                const neighborhood = selected.connectedNodes().union(selected);
                cy.animate({ fit: { eles: neighborhood, padding: 60 } } as any, { duration: 400 } as any);
            }
        },
        clearTxHighlight: () => {
            cyRef.current?.edges().removeClass('tx-selected');
        },
    }));

    const getLayoutConfig = (mode: RearrangeMode = 'flow'): any => {
        // Cytoscape built-in layouts (tender sheet #16). No ELK: these are
        // deterministic geometric arrangements, run through the same
        // rearrange path (pins respected, settle + fit afterwards).
        if (mode === 'circle') {
            return {
                name: 'circle',
                fit: true,
                padding: 40,
                avoidOverlap: true,
                spacingFactor: 1.2,
                sort: (a: any, b: any) => String(a.id()).localeCompare(String(b.id())),
                animate: true,
                animationDuration: 400,
            };
        }
        if (mode === 'grid') {
            return {
                name: 'grid',
                fit: true,
                padding: 40,
                avoidOverlap: true,
                avoidOverlapPadding: 30,
                condense: false,
                sort: (a: any, b: any) => String(a.id()).localeCompare(String(b.id())),
                animate: true,
                animationDuration: 400,
            };
        }
        // ELK layouts — four modes suited to different graph shapes:
        //   smart   → inspects the graph and picks the best of the three below
        //   flow    → layered LTR, best for clean directional tx flow
        //   organic → stress-majorization force, best for cyclic/dense graphs
        //   radial  → radial tree, best for hub-and-spoke patterns

        // Shared component-packing settings. Raised for dense forensics graphs:
        // wider gutters so crowded clusters don't collide with their neighbors.
        const commonSpacing = {
            'elk.spacing.nodeNode': 110,
            'elk.spacing.edgeNode': 60,
            'elk.spacing.edgeEdge': 35,
            'elk.separateConnectedComponents': 'true',
            'elk.spacing.componentComponent': 180,
            'elk.aspectRatio': 1.6,
            'elk.padding': '[top=60,left=60,bottom=60,right=60]',
        };
        let elkOpts: Record<string, any>;
        if (mode === 'organic') {
            // Stress layout: each edge has an "ideal length"; nodes settle
            // into positions that minimize total stress (edges not at ideal
            // length). For forensics graphs we want generous spacing so
            // highly-connected nodes don't pile on top of each other.
            elkOpts = {
                algorithm: 'stress',
                ...commonSpacing,
                'elk.stress.desiredEdgeLength': 240,   // pushes connected nodes further apart
                'elk.stress.iterationLimit': 500,
                'elk.stress.epsilon': 0.0001,
                'elk.spacing.nodeNode': 140,           // stricter hard min for stress
            };
        } else if (mode === 'radial') {
            elkOpts = {
                algorithm: 'radial',
                ...commonSpacing,
                'elk.radial.radius': 220,
                'elk.radial.rotate': 'false',
                'elk.spacing.nodeNode': 120,
            };
        } else {
            // flow (default) — layered LTR, heavily tuned for crossing reduction
            elkOpts = {
                algorithm: 'layered',
                'elk.direction': 'RIGHT',
                'elk.alignment': 'CENTER',
                'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
                'elk.layered.nodePlacement.favorStraightEdges': 'true',
                ...commonSpacing,
                'elk.layered.spacing.nodeNodeBetweenLayers': 140,       // more layer separation
                'elk.layered.considerModelOrder.strategy': 'NONE',
                'elk.layered.edgeRouting': 'ORTHOGONAL',
                'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
                'elk.layered.thoroughness': 20,                         // Reduced from 100 to prevent stack overflow
                'elk.layered.crossingMinimization.greedySwitch.activationThreshold': 40,
                'elk.layered.cycleBreaking.strategy': 'DEPTH_FIRST',    // More stable than GREEDY for complex graphs
                // CANVAS_PLACEMENT_LANGUAGE §10 — INTERACTIVE layering keeps
                // prior layer assignments stable across SSE step_done relayouts
                // when nodes declare placement_role (subject/source/sink/…).
                'elk.layered.layering.strategy': 'INTERACTIVE',
            };
        }
        return {
            name: 'elk',
            fit: true,
            padding: 40,
            animate: true,
            animationDuration: 400,
            animationEasing: 'ease-out-cubic',
            elk: elkOpts,
        };
    };

    /**
     * Analyze a Cytoscape element collection and choose the most appropriate
     * layout mode based on graph shape.
     *
     * Heuristics:
     *   - One node dominates with ≥40% of edges → radial (hub-and-spoke)
     *   - Density (edges/nodes) > 1.5 OR many self-cycles → organic
     *   - Otherwise → flow (clean DAG-ish structure)
     *
     * Also returns entity-type stats so callers can apply semantic anchors
     * (e.g. pin exchanges to the right in flow mode).
     */
    const analyzeGraph = (eles: any) => {
        const nodes = eles.nodes();
        const edges = eles.edges();
        const nodeCount = nodes.length;
        const edgeCount = edges.length;

        if (nodeCount === 0) return { mode: 'flow' as const, stats: null };

        // Degree map — how many edges touch each node
        const degree = new Map<string, number>();
        nodes.forEach((n: any) => degree.set(n.id(), n.degree(false)));

        const degrees = Array.from(degree.values());
        const maxDegree = Math.max(...degrees, 0);
        const avgDegree = edgeCount > 0 ? (2 * edgeCount) / nodeCount : 0;
        const density = nodeCount > 0 ? edgeCount / nodeCount : 0;

        // Hub detection: one node concentrates much of the connectivity.
        const hubShare = edgeCount > 0 ? maxDegree / edgeCount : 0;

        // Cycle hint: if any edge source appears as a target of its target node,
        // we have a small bidirectional loop. This is cheap and catches common
        // round-trip patterns.
        let bidirectionalEdges = 0;
        edges.forEach((e: any) => {
            const src = e.data('source');
            const tgt = e.data('target');
            if (src && tgt) {
                const reverse = edges.filter((r: any) =>
                    r.data('source') === tgt && r.data('target') === src
                );
                if (reverse.length > 0) bidirectionalEdges++;
            }
        });
        const cycleRate = edgeCount > 0 ? bidirectionalEdges / edgeCount : 0;

        // Entity-type distribution (for semantic anchoring later).
        const byType: Record<string, number> = {};
        nodes.forEach((n: any) => {
            const t = n.data('type') || n.data('entity_type') || 'unknown';
            byType[t] = (byType[t] || 0) + 1;
        });

        // CANVAS_PLACEMENT_LANGUAGE §3.2 + §10: when the user has pinned ≥2
        // anchors (deliberate spatial setup) OR any node declares
        // placement_role='subject', force `flow` (layered LTR) regardless of
        // cycle rate. The cycle-driven `organic` fallback is the failure
        // mode that produced the Holistic v2 column-pack — see
        // CANVAS_PLACEMENT_STUDIES.md §3 (debug-session 2026-05-06T19-17).
        const pinnedCount = manuallyPositionedNodeIdsRef.current.size;
        const hasSubjectRole = nodes.some(
            (n: any) => n.data('placement_role') === 'subject'
        );
        const placementOverride = pinnedCount >= 2 || hasSubjectRole;

        let chosen: 'flow' | 'organic' | 'radial' = 'flow';
        let reason = 'default — directional tx flow';
        if (placementOverride) {
            reason = hasSubjectRole
                ? 'placement_role=subject — honoring LTR flow'
                : `user pinned ${pinnedCount} anchors — honoring LTR flow`;
        } else if (nodeCount >= 4 && hubShare >= 0.4 && maxDegree >= 4) {
            chosen = 'radial';
            reason = `hub-and-spoke (one node holds ${Math.round(hubShare * 100)}% of edges)`;
        } else if (density > 1.5 || cycleRate > 0.15) {
            chosen = 'organic';
            reason = `dense/cyclic graph (density=${density.toFixed(1)}, cycles=${Math.round(cycleRate * 100)}%)`;
        }

        return {
            mode: chosen,
            stats: {
                nodeCount,
                edgeCount,
                density,
                maxDegree,
                avgDegree,
                hubShare,
                cycleRate,
                byType,
                reason,
                placementOverride,
                pinnedCount,
                hasSubjectRole,
            },
        };
    };


    const style = useMemo(() => [
        // ── Compound Nodes (Clusters) ──
        // Decorative gold fill was reserved for gold-path; clusters lift off
        // the canvas with a slate plate, not a neon wash.
        {
            selector: ':parent',
            style: {
                'background-opacity': isDark ? 0.35 : 0.18,
                'background-color': isDark ? '#27272a' : '#e2e8f0',
                'border-width': 2,
                'border-style': 'dashed',
                'border-color': isDark ? '#52525b' : '#94a3b8',
                'shape': 'round-rectangle',
                'padding': '20px',
                'text-valign': 'top',
                'text-halign': 'center',
                'font-size': '12px',
                'color': isDark ? '#a1a1aa' : '#475569',
                'text-background-opacity': 0,
                'label': (ele: any) => ele.data('label') || ele.data('entity_name') || 'Cluster'
            }
        },
        // ── Synthetic Limit Nodes (+X More) ──
        {
            selector: 'node.synthetic-cluster',
            style: {
                'shape': 'round-rectangle',
                'width': 120,
                'height': 30,
                'background-color': '#334155',  // slater-700
                'border-width': 1,
                'border-color': '#94a3b8',      // slate-400
                'border-style': 'dashed',
                'color': isDark ? '#cbd5e1' : '#475569',             // slate-300
                'font-size': '12px',
                'text-valign': 'center',
                'text-halign': 'center',
                'label': (ele: any) => ele.data('label') || 'Grouped Nodes'
            }
        },
        // ── Synthetic Link Edges ──
        // §10.3: synthetic links (UPI / off-chain inferred connections) are
        // dashed pink — they represent inference, not on-chain observation.
        // Same family as p2p-link / common-attribution / inferred / WEAK.
        {
            selector: 'edge.synthetic-link',
            style: {
                'width': 2,
                'line-color': '#ec4899',
                'line-style': 'dashed',
                'target-arrow-color': '#ec4899',
                'target-arrow-shape': 'triangle',
                'curve-style': 'bezier',
                'opacity': 0.7
            }
        },
        // ── Base node — rectangular card, dark background, white text ──────────
        {
            selector: 'node',
            style: {
                'shape': 'round-rectangle' as const,
                // §2.1 compact node target: ~120×40. Down from 160×50.
                // Variant nodes (Mixer/Mule/Hacker/Sanction/Court/Attribution
                // Hub) override these with their own width/height — those
                // shapes need extra real estate to register at glance.
                'width': 120,
                'height': 40,
                'background-color': isDark ? '#16171a' : '#ffffff',
                'border-width': 2,
                'border-color': isDark ? '#2a2b30' : '#cbd5e1',
                'color': isDark ? '#e4e4e7' : '#0f172a',
                'text-valign': 'center' as const,
                'text-halign': 'center' as const,
                // §4.1 single-line rule: collapse the legacy three-line label
                // (entity / chain+address / meta) to TWO lines max — primary
                // identity + a quiet truncated-address subline. Volume / tx-
                // count / velocity / category badges all moved to the Node
                // Inspector; they were unreadable at canvas zoom anyway.
                // 9b will land the risk dot top-right; 9e will replace the
                // textual chain prefix with a sprite glyph.
                'label': (ele: any) => {
                    const d = ele.data();
                    const intent = d.canvas_intent;

                    // R-CANVAS-INTENT: ambient-noise stays single-line
                    if (intent === CanvasNodeIntent.AMBIENT_NOISE) {
                        const addr: string = d.id || d.address || '';
                        return addr.length > 10 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
                    }

                    const addr: string = d.id || d.address || '';
                    const truncAddr = addr.length > 14
                        ? `${addr.slice(0, 8)}…${addr.slice(-4)}`
                        : addr;

                    const rawChain: string = typeof d.chain === 'string' && d.chain.length > 0 && d.chain.length <= 10
                        ? d.chain.toUpperCase()
                        : '';
                    const chain = rawChain && rawChain !== 'UNKNOWN' ? rawChain : '';
                    const addrLine = chain ? `${chain} ${truncAddr}` : truncAddr;

                    // Magic-address short-circuit: zero address + dEaD address
                    // get a canonical "Burn/Mint" label regardless of what
                    // the entity DB returned (some sources stamp these as
                    // "Null", "Null: 0x000…000", etc., which read as a real
                    // entity to officers — confusing). Skips the rest of
                    // the entity-name resolution.
                    if (isMagicAddress(addr)) {
                        return `${displayEntityName(addr, '')}\n${addrLine}`;
                    }

                    // Entity name: R-AUDIT prioritizes entity_name (live enrichment) over node_labels.
                    // §9j cleanup: skip flag-marker labels ('🚩 Flagged' /
                    // '🚩 Auto-flagged' / '⚠ Suspicious') when picking the
                    // fallback. Those are status metadata stamped by the
                    // backend triage chokepoint and the manual-flag handlers;
                    // the corner flag badge (slot 1 of bg-image) is the
                    // canonical visual indicator. Falling back to them as the
                    // *entity name* produced a duplicate "🚩 Flagged" line of
                    // text inside the card AND the corner badge — confusing.
                    const FLAG_LABEL_NOISE = new Set(['🚩 Flagged', '🚩 Auto-flagged', '⚠ Suspicious']);
                    let entityName: string | undefined = displayEntityName(addr, d.entity_name) || undefined;
                    if (!entityName) {
                        const nodeLabels: string[] | undefined = Array.isArray(d.node_labels) ? d.node_labels : undefined;
                        if (nodeLabels && nodeLabels.length > 0) {
                            const meaningful = nodeLabels.find(l => typeof l === 'string' && l.length > 0 && !FLAG_LABEL_NOISE.has(l.trim()));
                            if (meaningful) entityName = meaningful.trim();
                        }
                    }

                    const isSanctioned = d.entity_category === 'sanction'
                        || (Array.isArray(d.entity_tags) && d.entity_tags.some((t: unknown) => typeof t === 'string' && t.toLowerCase().includes('sanction')));
                    const isContract = d.is_contract === true;
                    const entityType: string | undefined = typeof d.entity_type === 'string' ? d.entity_type : undefined;

                    // Prefer explicit data.label / user_label when richer than a
                    // formatLabel mirror (demo teaching names; officer renames).
                    // Live formatLabel stores bare entity_name or trunc address —
                    // those fall through so the address subline still paints.
                    const storedLabel = resolveStoredCanvasLabel({
                        userLabel: d.user_label,
                        label: d.label,
                        entityName: entityName || d.entity_name,
                        address: addr,
                    });
                    if (storedLabel) {
                        const [first, ...rest] = storedLabel.split('\n');
                        let head = first;
                        if (d.is_root) head = '[ROOT] ' + head;
                        if (d.placement_role === 'source' || d.is_genesis_funder) head = '[SOURCE] ' + head;
                        if (d.is_frozen) head = '[FROZEN] ' + head;
                        else if (d.issuer_freeze_recommended || d.black_usdt) head = '[FREEZE-TARGET] ' + head;
                        if (isSanctioned) head = '[SANCTIONED] ' + head;
                        else if (isContract && !entityType) head = '[CONTRACT] ' + head;
                        return rest.length > 0 ? `${head}\n${rest.join('\n')}` : head;
                    }

                    // Inferred-identity attribution (when entity unknown but
                    // intelligence_attributions has high-confidence guess)
                    let attributionLabel = '';
                    const attributions = Array.isArray(d.intelligence_attributions) ? d.intelligence_attributions : [];
                    if (attributions.length > 0 && (!entityName || entityName.toLowerCase() === 'unknown')) {
                        const best = [...attributions].sort((a, b) => (b.confidence || 0) - (a.confidence || 0))[0];
                        if (best && (best.confidence || 0) >= 0.6) {
                            const dirPrefix = best.direction === 'inbound' ? '↓' : best.direction === 'outbound' ? '↑' : '';
                            attributionLabel = `? ${dirPrefix}${best.label}`;
                        }
                    }

                    // Build at most two lines: primary identity + addrLine subline.
                    if (entityName && entityName.toLowerCase() !== 'unknown') {
                        // Tighter cap than before (22→18) so a long entity
                        // name doesn't force Cytoscape to wrap into a third
                        // line and blow the 40px height budget.
                        let displayName = entityName.length > 18 ? entityName.slice(0, 17) + '…' : entityName;
                        if (d.is_root) displayName = '[ROOT] ' + displayName;
                        if (d.placement_role === 'source' || d.is_genesis_funder) displayName = '[SOURCE] ' + displayName;
                        if (d.is_frozen) displayName = '[FROZEN] ' + displayName;
                        else if (d.issuer_freeze_recommended || d.black_usdt) displayName = '[FREEZE-TARGET] ' + displayName;
                        if (isSanctioned) displayName = '[SANCTIONED] ' + displayName;
                        else if (isContract && !entityType) displayName = '[CONTRACT] ' + displayName;
                        return `${displayName}\n${addrLine}`;
                    }
                    if (attributionLabel) {
                        return `${attributionLabel}\n${addrLine}`;
                    }
                    if (isSanctioned) {
                        return `OFAC SANCTIONED\n${addrLine}`;
                    }
                    if (isContract) {
                        return `CONTRACT\n${addrLine}`;
                    }
                    // No entity context — single line, address only.
                    return addrLine;
                },
                'font-size': 10,
                'line-height': 1.2,
                'font-family': '"Noto Sans", system-ui, sans-serif',
                'text-wrap': 'wrap' as const,
                // 110px ≈ 18 chars at our font/size, matches the new 120px
                // node width minus 10px horizontal breathing room.
                'text-max-width': 110,
                'font-weight': '500',
                'transition-property': 'opacity, border-color, border-width, background-color',
                'transition-duration': '0.15s',

                // §9j.2 composite background. See header comment block
                // at top of file for the slot architecture.
                //
                // Four bg-image slots:
                // Slot 0 = 20px corner flag badge (top-right, 100% 0%)
                // Slot 1 = 16px "report generated" badge (bottom-right, 100% 100%, C5/F4)
                // Slot 2 = 18px issuer freeze badge (top-left, 0% 0%)
                // Slot 3 = 18px genesis funder / gas source badge (bottom-left, 0% 100%)
                'background-image': (ele: any) => [
                    getFlagUri(ele),
                    getReportBadgeUri(ele),
                    getFreezeBadgeUri(ele),
                    getSourceBadgeUri(ele),
                    getCertaintyBadgeUri(ele),
                    getNotAssessedBadgeUri(ele),
                ],
                'background-image-containment': 'over' as any,
                'background-fit': ['none', 'none', 'none', 'none', 'none', 'none'] as any,
                'background-width': ['20px', '16px', '18px', '18px', '14px', '14px'] as any,
                'background-height': ['20px', '16px', '18px', '18px', '14px', '14px'] as any,
                'background-position-x': ['100%', '100%', '0%', '0%', '50%', '50%'] as any,
                'background-position-y': ['0%', '100%', '0%', '100%', '100%', '0%'] as any,
                'background-clip': ['none', 'none', 'none', 'none', 'none', 'none'] as any,
            }
        },
        // ── Risk tier — three risk_score bg-image selectors removed
        // 2026-05-02 (sub-piece 9g). The corner risk dot now lives in the
        // BASE node selector's function-mapped `background-image` (slot 1).
        // See the header `§9b + §9g category-strip SVGs` block for why —
        // collapsing risk + strip into ONE bg-image function lets a node
        // show BOTH the entity-type left-strip and the risk dot at the
        // same time without the cascade replacing one with the other.
        // B4 quick-win (May 4 2026 audit): when nodes overlap on a
        // dense or low-zoom canvas, Cytoscape's default tap routing
        // resolves to whichever node was added LAST (traversal-order
        // top). Users reading the canvas with their eyes don't share
        // that mental model — they expect to hit the "important" node
        // (their root, an exchange, a sanctioned entity, an
        // attribution hub). The mismatch shows up as "I clicked S6
        // but the inspector loaded a different address" — the
        // B4 symptom the user reported.
        //
        // The ordering below establishes a hit-priority hierarchy.
        // Higher z-index wins in overlap. Numbers are deliberate gaps
        // (steps of 10) so future tiers can slot in without renumbering.
        //
        //   Tier 5 (z=50): root / target node — always on top.
        //   Tier 4 (z=40): sanctioned / OFAC.
        //   Tier 3 (z=30): named exchange / bridge.
        //   Tier 2 (z=20): entity-attributed (any name).
        //   Tier 1 (z=10): regular addresses (default).
        //
        // This does NOT solve all overlap cases (e.g. two equally-
        // ranked nodes still need a layout-side fix). It DOES solve
        // the most common user-frustration case where overlap costs
        // them the click on the actual subject of the investigation.
        { selector: 'node', style: { 'z-index': 10 } },
        { selector: 'node[?entity_name]', style: { 'z-index': 20 } },
        { selector: 'node[entity_type = "exchange"]', style: { 'z-index': 30 } },
        { selector: 'node[entity_type = "Exchange"]', style: { 'z-index': 30 } },
        { selector: 'node[entity_type = "bridge"]', style: { 'z-index': 30 } },
        { selector: 'node[entity_category = "sanction"]', style: { 'z-index': 40 } },
        { selector: 'node[?is_root]', style: { 'z-index': 50 } },

        // Critical band keeps its standalone selector below — it still
        // adds the ambient red fill, on top of the dot rendered by base.
        {
            selector: 'node[risk_level = "critical"]',
            style: {
                'border-color': '#ef4444',
                'border-width': 3,
                'background-color': isDark ? '#2d1b1b' : '#fef2f2',
            }
        },
        // ── Entity type — sub-piece 9g, 2026-05-02 ─────────────────────────
        // The seven round-rectangle entity types (Exchange / Service / DeFi
        // / Notable / P2P / SmartContract / Bridge) used to set their own
        // colored border + a subtle pastel background tint. With 9g the
        // category signal moves to a 4px coloured LEFT-STRIP rendered as
        // bg-image slot 0 (see `getStripUri` at the top of this file). The
        // border itself reverts to the base neutral slate (#475569 dark /
        // #cbd5e1 light) and the bg-color tint is dropped — the strip
        // alone carries the category, leaving the node face uncoloured so
        // risk (corner dot, slot 1) and forensic state (status pill, §9h)
        // can speak for themselves without competing with eight different
        // pastel washes. SmartContract keeps its `border-style: 'dashed'`
        // because the dashed line still reads at a glance as "this is
        // executable code, not a wallet". The other six selectors are
        // deleted entirely — the strip handles them.
        {
            selector: 'node[entity_type = "SmartContract"]',
            style: {
                'border-style': 'dashed' as const,
            }
        },
        // ── HIGH-DANGER types keep distinct shapes (visual alarm) ──────────────
        // Mixer: diamond, red on dark
        {
            selector: 'node[entity_type = "Mixer"]',
            style: {
                'shape': 'diamond' as const,
                'width': 44,
                'height': 44,
                'background-color': isDark ? '#2d1212' : '#fef2f2',
                'border-color': '#ef4444',
                'border-width': 3,
            }
        },
        // Mule: triangle. Light fill is the mermaid purple; the old rose wash is retired.
        {
            selector: 'node[entity_type = "Mule"]',
            style: {
                'shape': 'triangle' as const,
                'width': 44,
                'height': 44,
                ...paletteStyle('mule', isDark),
                'border-width': 3,
            }
        },
        // Hacker: star, red fill on dark
        {
            selector: 'node[entity_type = "Hacker"]',
            style: {
                'shape': 'star' as const,
                'width': 48,
                'height': 48,
                'background-color': isDark ? '#2d0f0f' : '#fef2f2',
                'border-color': '#ef4444',
                'border-width': 3,
            }
        },
        // Scam category: triangle warning
        {
            selector: 'node[entity_category = "scam"]',
            style: {
                'shape': 'triangle' as const,
                'width': 44,
                'height': 44,
                'background-color': isDark ? '#2d1212' : '#fef2f2',
                'border-color': '#ef4444',
                'border-width': 3,
            }
        },
        // Sanction (sub-piece 9d, 2026-05-02): was QUAD-encoded — star shape +
        // 4px dark-red border + red overlay glow + red fill + red text. Per
        // §1.3 (one strong signal beats four redundant ones) collapsed to a
        // single ambient encoding: background tint + label colour. The base
        // round-rectangle shape, the standard 120×40 size, and the risk-tier
        // border (#ef4444 width 2.5 from `node[risk_score >= 0.7]` higher up
        // in the stylesheet — sanctioned addresses always score critical) all
        // carry through unchanged. The [SANCTIONED] prefix in the label generator
        // (line ~806/814) keeps the in-text signal. Future: when 9b ships
        // the corner risk-dot and 9h ships the "OFAC" status pill, the
        // background fill here becomes redundant and can be dropped too.
        {
            selector: 'node[entity_category = "sanction"]',
            style: {
                'background-color': isDark ? '#3a0808' : '#fef2f2',
                'color': isDark ? '#fca5a5' : '#991b1b',
            }
        },
        // Court: diamond (legal hold)
        {
            selector: 'node[entity_category = "court"]',
            style: {
                'shape': 'diamond' as const,
                'width': 44,
                'height': 44,
                ...paletteStyle('court', isDark),
                'border-width': 3,
            }
        },
        // Gambling: round-rectangle. Sub-piece 9g (2026-05-02) moved the
        // orange category encoding from a full border-color into the 4px
        // left-strip (slot 0 of the base bg-image array) handled by
        // `getStripUri`. No selector body needed — the base node renders
        // the strip from data, and the rest (border, fill, label) inherits
        // base neutral. Kept as a placeholder selector + comment so the
        // intent is greppable.
        // ── Subcategory border-colour selectors REMOVED 2026-05-02 (Step 9c) ──
        // Eleven subcategory variants previously each took a distinct border
        // colour / style combo: hot_wallet amber-double, cold_wallet icy-blue
        // double, deposit teal, bridge orange-dashed, dex_router violet, otc
        // gold, p2p mauve, ponzi magenta, tornado_cash red-orange-double,
        // phishing yellow, indian_exchange saffron. Per §4 (visual debt #3)
        // and §1.3 (root cause: officers cannot learn 11 colour distinctions),
        // subcategory now surfaces as a TEXT chip in the Inspector header
        // (see NodeIdentityChips.tsx) instead of competing with the entity-
        // type border colour on the node face. The data property still
        // exists on every node — algorithms and the Inspector still read it.
        // Virtual attribution hubs — pink dashed to match common-attribution edges
        {
            selector: 'node[is_attribution_hub]',
            style: {
                'shape': 'round-octagon' as const,
                'background-color': isDark ? '#3a0a28' : '#fce7f3',
                'border-color': '#ec4899',
                'border-width': 2,
                'border-style': 'dashed',
                'width': 48,
                'height': 48,
                'color': '#ec4899',
            }
        },
        // Forensic overlay (Phase 2): subject-of-subpoena self address — purple double border
        {
            selector: 'node[forensic = "self"]',
            style: {
                'border-color': '#a855f7',
                'border-width': 3,
                'border-style': 'double',
            }
        },
        // Forensic overlay: counterparty seen in a subpoena record — purple solid, thinner
        {
            selector: 'node[forensic = "counterparty"]',
            style: {
                'border-color': '#a855f7',
                'border-width': 2,
            }
        },
        // Genesis Funder / Originating Source nodes (placement_role === 'source' or is_genesis_funder)
        {
            selector: 'node[placement_role = "source"], node[?is_genesis_funder], node.source-node',
            style: {
                'border-color': '#0d9488', // teal-600
                'border-width': 3,
                'border-style': 'solid',
                'background-color': isDark ? '#042f2e' : '#f0fdfa',
                'z-index': 35,
            }
        },
        // Issuer Frozen nodes (on-chain blacklist by Tether/Circle)
        {
            selector: 'node[?is_frozen], node.frozen-node',
            style: {
                'border-color': '#dc2626', // red-600
                'border-width': 3.5,
                'border-style': 'double',
                'background-color': isDark ? '#450a0a' : '#fef2f2',
                'z-index': 45,
            }
        },
        // Issuer Freeze Target / Black USDT laundering sink
        {
            selector: 'node[?issuer_freeze_recommended], node[?black_usdt]',
            style: {
                'border-color': '#ea580c', // orange-600
                'border-width': 3,
                'border-style': 'dashed',
                'background-color': isDark ? '#431407' : '#fff7ed',
                'z-index': 40,
            }
        },
        // Forensic edges: PROOF anchor — verified hash AND counterparty present.
        // This is the OnChain↔OffChain link event: the subpoena pinpoints exactly
        // which on-chain tx ties the customer to a specific external wallet.
        // Even ONE such edge in a case is the smoking gun.
        // §10.3: subpoena-rooted = blue stroke. Width snapped to §10.5 thick
        // tier (3.5px). The previous gold overlay is removed — gold is reserved
        // for trace-path membership only (§10.4). If a proof edge is ALSO on
        // the algorithm's trace path, the canonical onTracePath selector
        // composes its halo on top of this blue stroke.
        {
            selector: 'edge[forensic_edge = "proof"]',
            style: {
                'line-color': '#3b82f6',
                'target-arrow-color': '#3b82f6',
                'width': 3.5,
                'line-style': 'solid' as const,
                'arrow-scale': 1.6,
            }
        },
        // Forensic edges: verified by on-chain hash but no counterparty.
        // §10.3: still rooted in subpoena evidence → blue stroke. Width per
        // §10.5 medium tier.
        {
            selector: 'edge[forensic_edge = "verified"]',
            style: {
                'line-color': '#3b82f6',
                'target-arrow-color': '#3b82f6',
                'width': 2,
                'line-style': 'solid' as const,
            }
        },
        // Forensic edges: internal-only ID (no on-chain anchor).
        // §10.3: no on-chain observation → inferred → pink-dashed.
        {
            selector: 'edge[forensic_edge = "internal_only"]',
            style: {
                'line-color': '#ec4899',
                'target-arrow-color': '#ec4899',
                'width': 2,
                'line-style': 'dashed' as const,
                'opacity': 0.7,
            }
        },
        // Forensic ghost: counterparty the exchange did not disclose. The movement
        // happened (we have date/amount/hash), the wallet on the other side is
        // simply withheld. Render distinctly so investigators can see the data gap
        // — and so a future Tier-1a auto-link can promote ghost → real address.
        {
            selector: 'node[forensic = "ghost"]',
            style: {
                // Dark mode: slate-700 fill at 0.65 lifts the ghost off the
                // slate-900 canvas. Light mode: slate-200 fill at 0.7 reads
                // as a faint placeholder against the slate-50 canvas without
                // looking like a normal node. Border + dashed style + diamond
                // shape carry the "this is a placeholder" identity in both.
                'background-color': isDark ? '#334155' : '#e2e8f0',
                'background-opacity': isDark ? 0.65 : 0.7,
                'border-color': isDark ? '#94a3b8' : '#64748b',
                'border-width': 2,
                'border-style': 'dashed',
                'shape': 'round-diamond' as const,
                'color': isDark ? '#cbd5e1' : '#475569',
                'font-style': 'italic' as const,
            }
        },
        // §10.3: subpoena-rooted but counterparty withheld — the FLOW is still
        // observed on-chain (we have date/amount/hash), only the receiver is
        // hidden, so this stays neutral grey (money flow). Dashed because the
        // counterparty is uncertain. Snapped from #9ca3af to the canonical
        // grey #94a3b8 to consolidate the palette.
        {
            selector: 'edge[forensic_edge = "undisclosed"]',
            style: {
                'line-color': '#94a3b8',
                'target-arrow-color': '#94a3b8',
                'width': 1.5,
                'line-style': 'dashed' as const,
                'opacity': 0.65,
            }
        },
        // UTXO secondary edges: non-primary side of a multi-input/output tx.
        // Same selector applies to BOTH on-chain BTC fetches (services/fetchers/
        // btc.py marks utxo_expanded edges with utxo_secondary=true) AND
        // forensic subpoena rows that exploded comma-joined senders. Dotted line
        // signals "same tx as the solid edge of the same color" — tier color is
        // preserved so a proof-anchor secondary still reads as PROOF gold.
        // Solves the "co-spend linkage not visually represented" gap called out
        // in docs/FORENSIC_TRANSACTION_MODELS.md:25.
        {
            selector: 'edge[?utxo_secondary]',
            style: {
                'line-style': 'dotted' as const,
            }
        },
        // ── Starburst cluster: collapsed bundle for "many co-spenders of one tx
        //    converge on one node" patterns (CoinJoin, exchange batch, mining
        //    payout). Built canvas-side by utils/bundleStarburstEdges.ts.
        //    Tap → expand back to individual co-spender nodes + edges.
        //    Distinct slate-grey rounded-rectangle shape so it never reads as a
        //    real address node.
        {
            selector: 'node[type = "cluster"]',
            style: {
                'shape': 'round-rectangle' as const,
                'background-color': '#475569',          // slate-600
                'background-opacity': 0.85,
                'border-color': '#94a3b8',              // slate-400
                'border-width': 2,
                'border-style': 'dashed',
                'width': 110,
                'height': 36,
                'color': '#f8fafc',
                'font-size': 9,
                'font-weight': 700,
                'text-valign': 'center' as const,
                'text-halign': 'center' as const,
                'text-wrap': 'wrap' as const,
                'text-max-width': 100 as any,
            }
        },
        {
            selector: 'edge[?is_bundle]',
            style: {
                'line-color': '#94a3b8',                // slate-400
                'target-arrow-color': '#94a3b8',
                'source-arrow-color': '#94a3b8',
                'width': 3,
                'line-style': 'solid' as const,
                'opacity': 0.85,
                'curve-style': 'bezier' as const,
                'label': (ele: any) => ele.data('label') || '',
                'font-size': 9,
                'color': '#cbd5e1',
                'text-background-color': isDark ? '#1e293b' : '#f8fafc',
                'text-background-opacity': 0.92,
                'text-background-padding': '3px',
            }
        },
        // Smart contract: dashed border override
        {
            selector: 'node[is_contract]',
            style: {
                'border-style': 'dashed',
                'border-width': 2,
                'border-color': '#2563eb',
            }
        },
        // ── UPI nodes — tag shape, purple ──────────────────────────────────────
        {
            selector: 'node[type = "upi"]',
            style: {
                'shape': 'tag' as const,
                ...paletteStyle('identifier_upi', isDark),
                'border-width': 2,
                'width': 120,
                'height': 32,
                'label': 'data(id)',
                'font-size': 11,
                'line-height': 1.3,
                'color': '#c4b5fd',
            }
        },
        // ── Instagram OSINT social_handle hub — rounded-rect, pink-dashed ───────
        // Tier-4 OSINT lead, off-chain → pink/inferred family (§10.3). Dashed
        // border + distinct shape so it never reads as a real on-chain address.
        {
            selector: 'node[type = "social_handle"]',
            style: {
                'shape': 'round-rectangle' as const,
                'background-color': isDark ? '#2d1b2e' : '#fdf2f8',
                'background-opacity': 0.95,
                'border-color': '#ec4899',
                'border-width': 2,
                'border-style': 'dashed' as const,
                'width': 130,
                'height': 38,
                'label': 'data(label)',
                'font-size': 11,
                'font-weight': 700,
                'color': isDark ? '#f9a8d4' : '#be185d',
                'text-valign': 'center' as const,
                'text-halign': 'center' as const,
                'text-wrap': 'wrap' as const,
                'text-max-width': 120 as any,
            }
        },
        // OSINT pivot nodes (phone / email / cross-platform handle) — not addresses
        {
            selector: 'node[type = "phone"]',
            style: {
                'shape': 'ellipse' as const,
                ...paletteStyle('identifier_phone', isDark),
                'border-width': 2,
                'width': 70,
                'height': 32,
                'label': 'data(label)',
                'font-size': 10,
                'color': isDark ? '#7dba9a' : '#047857',
            }
        },
        {
            selector: 'node[type = "email"]',
            style: {
                'shape': 'round-rectangle' as const,
                ...paletteStyle('identifier_email', isDark),
                'border-width': 2,
                'width': 100,
                'height': 30,
                'label': 'data(label)',
                'font-size': 10,
                'color': isDark ? '#8aa0b8' : '#1d4ed8',
            }
        },
        {
            selector: 'node[type = "handle"]',
            style: {
                'shape': 'round-rectangle' as const,
                'background-color': isDark ? '#334155' : '#f1f5f9',
                'border-color': '#94a3b8',
                'border-width': 2,
                'border-style': 'dashed' as const,
                'width': 100,
                'height': 30,
                'label': 'data(label)',
                'font-size': 10,
                'color': isDark ? '#cbd5e1' : '#475569',
            }
        },
        // ── BTC TX nodes — diamond shape, amber, shows tx hash ──────────────────
        {
            selector: 'node[type = "transaction"]',
            style: {
                'shape': 'diamond' as const,
                'background-color': '#451a03',
                'border-color': '#f59e0b',
                'border-width': 2,
                'width': 36,
                'height': 36,
                'label': 'data(label)',
                'font-size': 9,
                'color': '#fbbf24',
                'text-margin-y': -4,
            }
        },
        // ── Privacy chain TX nodes — hexagon, purple, visually distinct from addresses ──
        {
            selector: 'node[type = "transaction"][is_privacy_chain]',
            style: {
                'shape': 'hexagon' as const,
                'background-color': '#1a0533',
                'border-color': '#7c3aed',
                'border-width': 2.5,
                'width': 42,
                'height': 42,
                'label': 'data(label)',
                'font-size': 9,
                'color': '#c084fc',
                'text-margin-y': -4,
            }
        },
        // ── Privacy chain address nodes — round-rectangle with purple tint ──
        {
            selector: 'node[type = "address"][is_privacy_chain]',
            style: {
                'border-color': '#7c3aed',
                'border-width': 2,
            }
        },
        // Analytics Overlays (Color + Size)
        {
            selector: 'node[overlayColor]',
            style: {
                'background-color': 'data(overlayColor)',
                'border-color': 'data(overlayColor)',
                'border-width': 3,
                'width': 'data(overlayWidth)',
                'height': 'data(overlayWidth)',
            }
        },
        // Edge styling - thin, precise, professional
        {
            selector: 'edge[label]',
            style: {
                'width': 1,
                'line-color': isDark ? '#71717a' : '#cbd5e1',
                'target-arrow-color': isDark ? '#71717a' : '#cbd5e1',
                'target-arrow-shape': 'triangle',
                'arrow-scale': 0.5,
                // Unbundled-bezier with an explicit control point gives every edge a
                // visible arc, which is what Metasleuth-style canvases get right and
                // we don't. `bezier` (the default we used pre-v2) only curves when
                // multiple edges run between the same pair, so on a sparse graph it
                // looks effectively straight. A single mid-line control point at
                // 40px perpendicular offset yields a moderate arc that scales OK
                // across short and medium edge lengths. See VISUAL_LANGUAGE_V2.md
                // Step 2. Per-type overrides below (.synthetic-link, [?is_bundle],
                // [edgeType="common-attribution"]) keep their own curve-style on
                // purpose — they're stylistic minorities with their own identity.
                'curve-style': 'unbundled-bezier',
                'control-point-distances': [40],
                'control-point-weights': [0.5],
                'label': 'data(label)',
                'text-opacity': 0.85,
                'font-size': 8,
                'color': isDark ? '#d4d4d8' : '#475569',
                'text-background-color': isDark ? '#16171a' : '#f8fafc',
                'text-background-opacity': 0.92,
                'text-background-padding': '3px',
                'text-border-color': isDark ? '#3f3f46' : '#cbd5e1',
                'text-border-width': 1,
                'text-border-opacity': 0.6,
                'font-family': '"Noto Sans", system-ui, sans-serif',
                'text-rotation': 'autorotate',
                // Lift the boxed value caption off the stroke so it reads as
                // an annotation flanking the line rather than sitting on top
                // of the arrow. Mirrors the gold-path label offset (which has
                // always done this) — see `edge[edgeType="gold-path"][label]`
                // below. Without this, the slate-grey box can fully occlude
                // the line and arrow at typical zoom on short edges.
                'text-margin-y': -10,
                'transition-property': 'line-color, width, text-opacity',
                'transition-duration': '0.15s'
            }
        },
        // Dust suppression — consolidator marks sub-floor edges with
        // `_hideLabel: true` (see `isDustEdge` in edgeConsolidation.ts).
        // Stroke + arrow still render so the relationship is visible; only
        // the value caption is dropped to keep low-zoom views readable.
        // Sits AFTER the base `edge[label]` rule so text-opacity wins.
        {
            selector: 'edge[?_hideLabel]',
            style: {
                'text-opacity': 0,
                'text-background-opacity': 0,
                'text-border-opacity': 0,
            }
        },
        // Show edge label on hover / selection with tx hash or consolidated details

        // ── §10.4 Trace-path overlay (the multi-class composition rule) ────────
        // An edge can simultaneously be (a) a member of a taxonomic class (grey
        // money-flow, blue subpoena, pink-dashed inferred, violet DEX) AND (b)
        // on the algorithm's chosen trace path. Today's gold-path selectors
        // REPLACE the taxonomic stroke with amber, losing the "what KIND of
        // edge" signal. §10.4 fixes this by rendering trace-path membership as
        // a gold OVERLAY HALO on top of whatever stroke the edge already has.
        //
        // Canonical signal: `data(onTracePath)`. Legacy signals (`gold-path`
        // class, `edgeType:"gold-path"`, `edgeType:"gold-path-connector"`,
        // `importance:"critical"`) are kept for backward compat with saved
        // snapshots and existing call sites; they all funnel into the same
        // visual rule below. New code should write `onTracePath: true` only.
        //
        // What this rule does NOT do:
        //   - No `line-color` (taxonomic stroke wins per §10.4)
        //   - No `width` (magnitude tier wins per §10.5)
        //   - No `target-arrow-color` (inherits from base/taxonomic stroke)
        // What it DOES do: gold overlay (same amber as the gold-path stroke,
        // low opacity — not a neon bloom) + raised z-index + readable amber label.
        {
            selector: 'edge[?onTracePath], edge[importance = "critical"][label], edge.gold-path[label], edge[edgeType = "gold-path"][label], edge[edgeType = "gold-path-connector"][label]',
            style: {
                'overlay-color': '#f59e0b',
                'overlay-opacity': 0.12,
                'overlay-padding': 2,
                'z-index': 999,
                'text-opacity': 1,
                'label': 'data(label)',
                'font-weight': '700',
                'line-style': 'solid',
            }
        },
        {
            // §10.11 compliant — no stroke-color override on interaction states.
            // Width + label-style + z-index communicate "high importance" while
            // taxonomic stroke (grey/blue/pink/violet) is preserved. Magnitude
            // tier (§10.5) usually owns width on consolidated edges; this rule
            // wins only on legacy `importance="high"` edges that don't carry
            // a `_widthTier` (e.g. backend-emitted importance markers).
            selector: 'edge[importance = "high"][label], edge.edge-emphasized[label], edge.edge-animated[label]',
            style: {
                'width': 2,
                'z-index': 998,
                'text-opacity': 0.95,
                'label': 'data(label)',
                'font-weight': '600',
                'line-style': 'solid',
            }
        },
        {
            // §10.11 compliant — opacity + line-style communicate "low importance"
            // without changing stroke colour (taxonomy preserved).
            selector: 'edge[importance = "low"], edge.edge-deemphasized',
            style: {
                'width': 0.5,
                'line-style': 'dashed',
                'text-opacity': 0, // Reduces noise on low priority paths
            }
        },
        // §10.5 Magnitude tier — three discrete widths keyed on MAX of
        // (value-tier, count-tier). Computed in edgeConsolidation.ts and
        // written to `_widthTier` on every consolidated edge. These selectors
        // sit AFTER the importance-based width rules so they win on
        // consolidated edges (consistent with §10's plan to retire the
        // importance bucket), but BEFORE per-type stylistic overrides
        // (gold-path, dex-swap, p2p-link, common-attribution, synthetic-link)
        // which keep their identity widths until sub-piece #2 collapses them
        // into overlay halos.
        { selector: 'edge[_widthTier = "thin"]',   style: { 'width': 1.0 } },
        { selector: 'edge[_widthTier = "medium"]', style: { 'width': 2.0 } },
        { selector: 'edge[_widthTier = "thick"]',  style: { 'width': 3.5 } },
        // WB06: an edge whose value could not be read on any USD basis (no USD
        // figure, no stablecoin, no priced native) is drawn thin AND dashed, so a
        // thin line is not mistaken for a small transfer. Type-specific rules
        // below keep their own line style.
        { selector: 'edge[_valueBasis = "unknown"]', style: { 'line-style': 'dashed' } },
        {
            // §10.11: Cytoscape's native click-select feedback. Stroke colour
            // override REMOVED so the underlying taxonomic stroke survives a
            // click. Selection is communicated via a muted blue overlay + width
            // bump + raised z-index. Nodes use fill lightness instead of overlay.
            selector: 'edge:active, edge:selected',
            style: {
                'text-wrap': 'wrap',
                'text-opacity': 1,
                'width': 1.5,
                'overlay-color': '#3b82f6',
                'overlay-opacity': 0.12,
                'overlay-padding': 2,
                'z-index': 1000,
            }
        },
        // §10.11: Edge hover. Cytoscape has no `:hover` pseudo-selector, so
        // hover relies on a JS handler (cy.on mouseover/mouseout 'edge')
        // which adds/removes `.edge-hover`. Style is overlay-only per spec.
        {
            selector: 'edge.edge-hover',
            style: {
                'overlay-color': '#3b82f6',
                'overlay-opacity': 0.12,
                'overlay-padding': 2,
                'z-index': 90,
            }
        },
        // edge[type="STRONG"|"WEAK"] selectors removed 2026-05-02. The backend
        // p2p endpoint emits {"type":"STRONG"|"WEAK"} on linked-address records
        // (api/v1/p2p.py:536) but no frontend code translates those records
        // into edge data with `type` set, so these selectors had no consumers.
        // The strong/weak distinction now lives in p2p-link's pink-dashed
        // styling + the existing inspector-side STRONG/WEAK label rendering.
        // Per §10.3 (no red/orange in the 4-colour palette).
        // ── §10.7 / §10.11 Interaction-state visual signals ───────────────────
        // Per §10.11: "Selection is opacity-only. Hover is opacity-only. The
        // taxonomic color of an edge does not change when the user interacts
        // with it." Today these classes overrode `line-color` and arrow color,
        // which destroyed the taxonomic stroke (grey money-flow / blue subpoena
        // / pink-dashed inferred / violet DEX). Stroke-color overrides are now
        // REMOVED. Interaction feedback on edges is a single muted-blue overlay
        // (#3b82f6 at 0.12), plus modest width bumps and z-index raise. Nodes
        // lift via background-blacken (lightness), not overlay. The §10
        // canonical opacity channel is the already-shipped R11 `_visibility`
        // data property (constants/visibility.ts) — we don't introduce a
        // separate `_focusState` data property because R11 already provides
        // exactly what §10.7 specifies (1.0 / 0.55 / 0.0 driven by data prop).
        // Migration of the addClass/removeClass call sites to use _visibility
        // directly is follow-up work; the visual cleanup ships today.
        {
            selector: 'edge.edge-animated',
            style: {
                'width': 2,
                'target-arrow-shape': 'triangle-backcurve',
                'arrow-scale': 0.7,
                'z-index': 999,
                'overlay-color': '#3b82f6',
                'overlay-opacity': 0.12,
                'overlay-padding': 2
            }
        },
        {
            selector: 'edge.edge-emphasized',
            style: {
                'width': 1.5,
                'overlay-color': '#3b82f6',
                'overlay-opacity': 0.12,
                'overlay-padding': 2,
                'text-opacity': 1,
                'font-weight': '600',
                'z-index': 100,
            }
        },
        {
            selector: 'edge.edge-deemphasized',
            style: {
                'width': 0.6,
                'label': ''
            }
        },
        {
            selector: 'edge.edge-focused',
            style: {
                'width': 1.5,
                'overlay-color': '#3b82f6',
                'overlay-opacity': 0.12,
                'overlay-padding': 2,
                'text-opacity': 1,
                'z-index': 100,
            }
        },
        {
            selector: 'edge.edge-unfocused',
            style: {
                'width': 0.5
            }
        },
        {
            selector: 'edge.edge-multi-selected',
            style: {
                'width': 1.5,
                'overlay-color': '#3b82f6',
                'overlay-opacity': 0.12,
                'overlay-padding': 2,
                'text-opacity': 1,
                'z-index': 120,
            }
        },
        {
            // Single-click selection feedback (Transfers tab sync). Overlay
            // is the §10.11-compliant signal; stroke colour is taxonomy.
            selector: 'edge.edge-single-selected',
            style: {
                'width': 3,
                'text-opacity': 1,
                'overlay-color': '#3b82f6',
                'overlay-opacity': 0.12,
                'overlay-padding': 2,
                'z-index': 150,
            }
        },
        // Bidirectional sync: tx checkbox selection highlight
        {
            selector: 'edge.tx-selected[label]',
            style: {
                'width': 2,
                'opacity': 1,
                'z-index': 100,
                'overlay-color': '#3b82f6',
                'overlay-opacity': 0.12,
                'overlay-padding': 2,
                'label': 'data(label)',
                'font-size': 11,
                'line-height': 1.3,
                'text-opacity': 1,
            }
        },
        // Bidirectional sync: hover cross-highlight
        {
            selector: 'node.hover-emphasis',
            style: {
                'border-width': 3,
                'border-color': '#3b82f6',
                'opacity': 1,
                'z-index': 100,
            }
        },
        {
            selector: 'edge.hover-emphasis',
            style: {
                'opacity': 1,
                'width': 1.5,
                'z-index': 100,
            }
        },
        // §10.4 hybrid (option B, May 5): a pale-yellow overlay alone made
        // trace-path edges indistinguishable from regular money-flow edges.
        // Overlay is now the same amber as the gold-path stroke (#f59e0b at
        // 0.12) — a lightness lift along the path, not a neon bloom. Until
        // the multi-axis overlap case exists at scale, restore the amber
        // stroke for PURE trace-path edges (those with no competing
        // taxonomic stroke).
        //
        // The discriminator is `edgeType = "gold-path"` / `gold-path-connector` /
        // `.gold-path` class — these mark an edge whose ONLY identity is being a
        // trace path. Edges that also carry a taxonomic edgeType (subpoena-anchor,
        // dex-swap, inferred, etc.) signal trace membership only via
        // `data(onTracePath)` and stay halo-only — the taxonomic stroke wins,
        // halo composes on top. So §10.4's multi-signal architecture survives;
        // only the pure-trace case gets the obvious gold stroke back.
        {
            selector: 'edge[edgeType = "gold-path"], edge[edgeType = "gold-path-connector"], edge.gold-path',
            style: {
                'line-color': '#f59e0b',          // amber — reads as "trace path" at a glance
                'target-arrow-color': '#f59e0b',
                'target-arrow-shape': 'triangle-backcurve',
                'arrow-scale': 0.7,
            },
        },
        // Gold Path edges (Auto-Trace result) - Refined styling
        {
            selector: 'edge[edgeType = "gold-path"][label]',
            style: {
                // Stroke (width, line-style) intentionally REMOVED — taxonomic stroke
                // and magnitude tier (§10.5) own those channels. Trace-path membership
                // is communicated by the gold overlay halo set by the canonical
                // §10.4 selector above + the gold stroke from the §10.4-hybrid
                // selector above (option B fallback for pure trace edges).
                // This block now only provides label-level identity (amber text,
                // dark amber background) so trace-path edge labels remain quickly
                // scannable.
                'label': 'data(label)',
                'text-opacity': 1,
                'font-weight': '500',
                'font-size': 8,
                'color': '#fbbf24',
                'text-margin-y': -10,
                'text-background-color': isDark ? '#1c1a0e' : '#fef3c7',
                'text-background-opacity': 0.92,
                'text-background-padding': '3px',
                'text-border-color': isDark ? '#854d0e' : '#d97706',
                'text-border-width': 1,
                'text-border-opacity': 0.5,
            }
        },
        // Gold Path nodes (part of Auto-Trace result). Elevation via a
        // slightly lifted fill (background-blacken) plus the amber border —
        // no overlay bloom.
        {
            selector: 'node[isGoldPath]',
            style: {
                'border-width': 2.5,
                'border-color': '#f59e0b',
                'background-blacken': isDark ? -0.14 : 0.05,
            }
        },
        // Counterfeit-token / look-alike-dust evidence edges (NS-09): muted, dashed, not money flow.
        ...counterfeitEdgeStyles(isDark),
        ...ledgerEdgeStyles(isDark),
        // P2P link edges (UPI connections)
        {
            // p2p-link (UPI ↔ crypto) is semantically an inferred / off-chain
            // link, NOT a swap. §10.3 maps inferred edges to pink-dashed and
            // swaps to violet — the previous violet here mis-implied "DEX
            // swap." Recoloured to pink-dashed (#ec4899), same family as
            // common-attribution and synthetic-link.
            selector: 'edge[edgeType = "p2p-link"][label]',
            style: {
                'width': 1,
                'line-color': '#ec4899',
                'target-arrow-color': '#db2777',
                'line-style': 'dashed',
                'label': 'data(label)',
                'text-opacity': 1,
                'font-size': 7
            }
        },
        // Common-attribution edges — speculative link between addresses that
        // share the same entity attribution (e.g. multiple Binance deposits).
        // Dashed pink to match the "inferred/speculative" convention (same
        // visual family as p2p-link / synthetic-link / WEAK edges).
        {
            selector: 'edge[edgeType = "common-attribution"][label]',
            style: {
                'width': 1.5,
                'line-color': '#ec4899',
                'target-arrow-color': '#db2777',
                'target-arrow-shape': 'none',
                'source-arrow-shape': 'none',
                'curve-style': 'bezier',
                'line-style': 'dashed',
                'opacity': 0.8,
                'label': 'data(label)',
                'text-opacity': 1,
                'font-size': 8,
                'color': '#ec4899',
                'text-background-color': isDark ? '#1e293b' : '#f8fafc',
                'text-background-opacity': 0.92,
                'text-border-color': '#be185d',
                'text-border-width': 1,
                'text-border-opacity': 0.7,
                'z-index': 5,
            }
        },
        // §10.3 Tier-1a / proof-anchor edges — subpoena-verified on-chain
        // movement. Blue stroke is the §10 taxonomic colour for "this edge
        // came from a court-process evidence document, not from blockchain
        // observation alone." Detection: any edge with `forensic_record_id`
        // (covers both verified Tier-1a and ghost Tier-1b counterpart rows).
        // Width still owned by §10.5 magnitude tier; trace overlay still
        // composes via §10.4 if the edge is also on the algorithm's path.
        // The Inspector-side purple forensic indicators (TransfersTab row
        // tint, Verified badge) intentionally stay purple for now — those
        // are an inspector-row identity; the canvas-edge identity is blue.
        // Aligning the two surfaces is a follow-up UX call.
        {
            selector: 'edge[?forensic_record_id]',
            style: {
                'line-color': '#3b82f6',
                'target-arrow-color': '#3b82f6',
            }
        },
        // DEX swap edges — token-to-token trades (matched by tx_hash)
        {
            selector: 'edge[edgeType = "dex-swap"][label]',
            style: {
                'width': 1.5,
                'line-color': '#8b5cf6',
                'target-arrow-color': '#7c3aed',
                'target-arrow-shape': 'triangle-backcurve',
                'arrow-scale': 0.7,
                'z-index': 50,
                'label': 'data(label)',
                'text-opacity': 1,
                'font-weight': 500,
                'font-size': 8,
                'color': '#a78bfa',
                'text-margin-y': -10,
                'text-background-color': isDark ? '#1a1028' : '#f5f3ff',
                'text-background-opacity': 0.92,
                'text-background-padding': '3px',
                'text-border-color': isDark ? '#5b21b6' : '#7c3aed',
                'text-border-width': 1,
                'text-border-opacity': 0.5,
                'text-wrap': 'wrap',
            }
        },
        // Same-chain stablecoin swap (USDT↔USDD / Curve 3Pool). Teal distinct
        // from violet DEX swaps — taxonomy owns colour (R23).
        {
            selector: 'edge[edgeType = "stablecoin-swap"]',
            style: {
                'width': 2,
                'line-color': '#14b8a6',
                'target-arrow-color': '#0d9488',
                'target-arrow-shape': 'triangle-backcurve',
                'arrow-scale': 0.7,
                'z-index': 50,
                'label': (ele: any) => {
                    const pair = ele.data('swap_pair_hint');
                    const proto = ele.data('swap_protocol');
                    if (pair && proto) return `${pair}\nvia ${proto}`;
                    if (pair) return String(pair);
                    if (proto) return `stable ↔ ${proto}`;
                    return 'stablecoin swap';
                },
                'font-weight': 500,
                'font-size': 8,
                'color': isDark ? '#5eead4' : '#0f766e',
                'text-margin-y': -10,
                'text-background-color': isDark ? '#042f2e' : '#f0fdfa',
                'text-background-opacity': 0.92,
                'text-background-padding': '3px',
                'text-border-color': isDark ? '#0f766e' : '#14b8a6',
                'text-border-width': 1,
                'text-border-opacity': 0.5,
                'text-wrap': 'wrap',
            }
        },
        // ── UTXO input edges (address → TX node) ─────────────
        // §10.3: still on-chain money flow → neutral grey. Dashed because
        // the input side of a multi-input tx is one of several converging
        // contributions (inherent ambiguity per UTXO model).
        {
            selector: 'edge[edgeType = "utxo-input"]',
            style: {
                'width': 1.5,
                'line-color': '#94a3b8',
                'line-style': 'dashed' as const,
                'target-arrow-color': '#94a3b8',
                'target-arrow-shape': 'triangle',
                'arrow-scale': 0.6,
            }
        },
        // ── UTXO output edges (TX node → address) ─────────────
        // §10.3: still on-chain money flow → neutral grey, solid.
        {
            selector: 'edge[edgeType = "utxo-output"]',
            style: {
                'width': 2,
                'line-color': '#94a3b8',
                'target-arrow-color': '#94a3b8',
                'target-arrow-shape': 'triangle',
                'arrow-scale': 0.8,
            }
        },
        {
            selector: ':selected',
            style: {
                'z-index': 999
            }
        },
        // Focus mode: dim non-selected nodes when something is selected
        {
            selector: 'node:unselected',
            style: {
                // Default opacity - will be overridden when selection exists
            }
        },
        // §10.3 + §10.6: inferred/weak links — pink-dashed. Same family as
        // p2p-link, common-attribution, synthetic-link, internal_only.
        {
            selector: 'edge[edgeType = "inferred"]',
            style: {
                'line-style': 'dashed',
                'line-dash-pattern': [4, 3],
                'width': 1.5,
                'line-color': '#ec4899',
                'target-arrow-color': '#ec4899',
            }
        },
        // OSINT attribution edges — Instagram social_handle → crypto/UPI lead.
        // Off-chain Tier-4 claim → pink/inferred family (§10.3). Tighter dash
        // pattern distinguishes it from p2p-link / inferred at a glance.
        {
            selector: 'edge[edgeType = "osint-link"]',
            style: {
                'line-style': 'dashed' as const,
                'line-dash-pattern': [2, 4],
                'width': 1.5,
                'line-color': '#ec4899',
                'target-arrow-color': '#ec4899',
                'target-arrow-shape': 'triangle' as const,
                'arrow-scale': 0.7,
                'opacity': 0.85,
                'curve-style': 'bezier' as const,
            }
        },
        {
            selector: 'edge[edgeType = "osint-link"][label]',
            style: {
                'label': 'data(label)',
                'font-size': 9,
                'color': isDark ? '#f9a8d4' : '#be185d',
                'text-background-color': isDark ? '#1e293b' : '#fdf2f8',
                'text-background-opacity': 0.9,
                'text-background-padding': '2px',
            }
        },
        // Bridge-hop edges — cross-chain movement.
        // Amber DASHED (#f59e0b), as the tender sheet (#10) specifies. Trace
        // paths keep the same amber but SOLID, so the dash is what separates
        // "crossed a bridge" from "on the traced path". Pre-Nov 2026 (§10.3)
        // this was neutral grey with the identity carried by the label only.
        {
            selector: 'edge[edgeType = "bridge-hop"]',
            style: {
                'line-color': '#f59e0b',
                'line-style': 'dashed',
                'line-dash-pattern': [8, 4],
                'width': 2.5,
                'target-arrow-color': '#f59e0b',
                'target-arrow-shape': 'triangle',
                'label': (ele: any) => {
                    const fam = ele.data('bridge_family');
                    const target = ele.data('bridge_target_chain');
                    if (fam && target && target !== 'MULTI') return `${fam} → ${target}`;
                    if (fam) return fam;
                    return 'bridge';
                },
                'font-size': 9,
                'color': isDark ? '#cbd5e1' : '#475569',
                'text-background-color': isDark ? '#1e293b' : '#f8fafc',
                'text-background-opacity': 0.92,
                'text-background-padding': 2,
            }
        },
        // Privacy / mixer / CoinJoin edges — purple DASHED (#a855f7), as the
        // tender sheet (#10) specifies. DEX swaps keep violet #8b5cf6 SOLID;
        // the brighter purple plus the dash keeps the two apart. Pre-Nov 2026
        // (§10.3) these were neutral grey.
        {
            selector: 'edge[edgeType = "mixer-deposit"]',
            style: {
                'line-color': '#a855f7',
                'line-style': 'dashed',
                'line-dash-pattern': [6, 3],
                'width': 2.5,
                'target-arrow-color': '#a855f7',
                'target-arrow-shape': 'triangle',
                'label': (ele: any) => {
                    const fam = ele.data('privacy_family');
                    const pool = ele.data('privacy_pool_size');
                    if (fam && pool) return `${fam} ${pool} ETH →`;
                    if (fam) return `${fam} deposit`;
                    return 'mixer deposit';
                },
                'font-size': 9,
                'color': isDark ? '#cbd5e1' : '#475569',
                'text-background-color': isDark ? '#1e293b' : '#f8fafc',
                'text-background-opacity': 0.92,
                'text-background-padding': 2,
            }
        },
        {
            // Purple dashed (sheet #10). Dashed also because the
            // privacy-pool exit is partially attribution-uncertain (the
            // bridge from the obfuscated middle to this output is heuristic).
            selector: 'edge[edgeType = "mixer-withdrawal"]',
            style: {
                'line-color': '#a855f7',
                'line-style': 'dashed',
                'line-dash-pattern': [4, 3],
                'width': 2.5,
                'target-arrow-color': '#a855f7',
                'target-arrow-shape': 'triangle',
                'label': (ele: any) => {
                    const fam = ele.data('privacy_family');
                    if (fam) return `← ${fam} withdrawal`;
                    return '← mixer withdrawal';
                },
                'font-size': 9,
                'color': isDark ? '#cbd5e1' : '#475569',
                'text-background-color': isDark ? '#1e293b' : '#f8fafc',
                'text-background-opacity': 0.92,
            }
        },
        {
            // §10.3: sanctions identity is carried by the destination NODE's
            // red dot per §2.4, NEVER by the edge stroke ("the flow isn't
            // sanctioned, the destination is"). The edge stays mixer purple; only
            // the width bumps up to the §10.5 thick tier so the relationship
            // is still visually weighted on the canvas.
            selector: 'edge[?privacy_sanctioned][edgeType = "mixer-deposit"]',
            style: {
                'line-color': '#a855f7',
                'target-arrow-color': '#a855f7',
                'width': 3.5,
            }
        },
        // CoinJoin pattern edges — co-spend obfuscation (NOT sanctioned).
        // Purple dashed like the other privacy edges (sheet #10); dashed also
        // because attribution through the CoinJoin batch is probabilistic.
        {
            selector: 'edge[edgeType = "coinjoin-pattern"]',
            style: {
                'line-color': '#a855f7',
                'line-style': 'dashed',
                'line-dash-pattern': [4, 2],
                'width': 2,
                'target-arrow-color': '#a855f7',
                'target-arrow-shape': 'triangle',
                'label': (ele: any) => {
                    const fam = ele.data('privacy_family');
                    if (fam) return `${fam} CoinJoin`;
                    return 'CoinJoin';
                },
                'font-size': 9,
                'color': isDark ? '#cbd5e1' : '#475569',
                'text-background-color': isDark ? '#1e293b' : '#f8fafc',
                'text-background-opacity': 0.92,
            }
        },
        // Consolidated edge labels — "N txs · volume TOKEN"
        // Hidden at rest (opacity 0), revealed on hover/select so they don't clutter the canvas
        {
            selector: 'edge[?is_consolidated]',
            style: {
                'label': (ele: any) => {
                    const d = ele.data();
                    const count: number = d.tx_count || 1;
                    const token: string = d.token_symbol || '';
                    
                    // Multi-token: show per-token breakdown
                    if (token.includes(', ')) {
                        const tokens = token.split(', ');
                        return `${count} txs · ${tokens.length} tokens`;
                    } else {
                        const val: number = d.total_value || d.value || 0;
                        const fmtValue = formatCurrency(val, token);
                        return count === 1 ? fmtValue : `${count} txs · ${fmtValue}`;
                    }
                },
                'text-opacity': 0,  // hidden by default — shown on :selected/:active below
                'font-size': 9,
                'color': isDark ? '#cbd5e1' : '#475569',
                'text-background-color': isDark ? '#1e293b' : '#f8fafc',
                'text-background-opacity': 0.92,
                'text-background-padding': '3px',
                'text-border-color': isDark ? '#334155' : '#cbd5e1',
                'text-border-width': 1,
                'text-border-opacity': 0.6,
                'width': 1.5,
            }
        },
        // Show consolidated label on hover / selection
        {
            selector: 'edge[?is_consolidated]:selected, edge[?is_consolidated]:active',
            style: { 'text-opacity': 1 }
        },
        // ── Data-driven visibility (Phase A) ──────────────────────────
        // `_visibility` data property (0.0–1.0) controls opacity and events.
        // Set by: graph filters, eye toggle, Follow Money, SSE discovering.
        // v5.3: legacy CSS classes (.node-filtered-out, .edge-filtered-out,
        // .follow-money-dimmed) deleted — everything flows through _visibility.
        {
            selector: '[_visibility > 0][_visibility < 1]',
            style: {
                'opacity': 'data(_visibility)' as any,
            }
        },
        // ── Canvas Intent: Nodes ──
        {
            selector: `node[canvas_intent = "${CanvasNodeIntent.TARGET_ROOT}"]`,
            style: {
                'width': 175,
                'height': 60,
                'border-width': 4,
                'border-color': '#f59e0b', // Amber-500
                'border-style': 'double',
                'background-color': isDark ? '#451a03' : '#fffbeb',
                'z-index': 1000,
                'font-weight': '700',
                'text-max-width': 165,
                'font-size': 12,
            }
        },
        {
            selector: `node[canvas_intent = "${CanvasNodeIntent.CRITICAL_ENDPOINT}"]`,
            style: {
                'width': 165,
                'height': 56,
                'border-width': 3,
                'border-style': 'solid',
                // Red only when the risk says so; unassessed nodes read neutral
                // grey (canvasAssurance.criticalEndpointBorderColor).
                'border-color': (ele: any) => criticalEndpointBorderColor(ele.data()),
                'background-blacken': isDark ? -0.08 : 0.04,
                'font-weight': '700',
                'text-max-width': 155,
            }
        },
        {
            selector: `node[canvas_intent = "${CanvasNodeIntent.HEAVY_CONTEXT}"]`,
            style: {
                'border-width': 2.5,
                'border-color': '#3b82f6', // Blue-500
                'border-style': 'solid',
                'background-color': isDark ? '#172554' : '#eff6ff',
                'font-weight': '500',
            }
        },
        // Exchange is orange. The blue above is a heavy-context wallet or an email, not an exchange.
        {
            selector: 'node[entity_type = "Exchange"], node[entity_type = "exchange"], node[entity_category = "exchange"]',
            style: { ...paletteStyle('cex', isDark), 'border-width': 2 },
        },
        {
            selector: 'node[wallet_role = "exchange_deposit"][confirmation_state = "registry_list"], node[wallet_role = "exchange_deposit"][confirmation_state = "exchange_confirmed"], node[wallet_role = "deposit"][confirmation_state = "registry_list"], node[wallet_role = "deposit"][confirmation_state = "exchange_confirmed"]',
            style: { ...paletteStyle('cex_deposit_verified', isDark), 'border-width': 2 },
        },
        {
            selector: 'node[entity_type = "Victim"], node[entity_type = "victim"], node[entity_category = "victim"]',
            style: { ...paletteStyle('victim', isDark), 'border-width': 2 },
        },
        {
            selector: 'node[entity_category = "card_offramp"], node[entity_type = "Card"], node[wallet_role = "card_offramp"]',
            style: { ...paletteStyle('card', isDark), 'border-width': 2 },
        },
        {
            selector: `node[canvas_intent = "${CanvasNodeIntent.AMBIENT_NOISE}"]`,
            style: {
                'border-width': 1,
                'border-style': 'dashed',
                'border-color': isDark ? '#475569' : '#94a3b8',
                'font-size': 9,
                'font-weight': '400',
            }
        },
        // Package 8: never-assessed nodes (grey dashed border + "?" badge),
        // after the intent rules so it wins over the tier colour.
        ...buildAssuranceNodeStyles(),
        {
            // §9j cleanup (May 2026):
            // The DANGER intent used to paint ambient red bg + red overlay
            // on top of size emphasis + bold border. Two problems with the
            // ambient-bg approach in a post-§9j world:
            //   (a) Auto-trace tagged any terminal as DANGER when the
            //       OVERALL trace target was sanctions/mixer/etc. — innocent
            //       intermediate exchanges (Coinbase, OKX deposit, MEXC)
            //       that happened to be path terminals got painted pink,
            //       reading as "this exchange is dangerous" when they're
            //       not. Risk now lives on the FLAG (auto_flagged tag), not
            //       on a path-position-based intent.
            //   (b) The hardcoded `overlay-color: '#ef4444'` overrode
            //       Cytoscape's default `:grabbed` overlay, so DANGER nodes
            //       gave NO drag-feedback to the user (the grey "I picked
            //       this up" overlay never showed). Removing the overlay
            //       restores standard grab/drag visuals.
            // What stays: size emphasis + bold border + high z-index.
            // These are the intent's UNIQUE value — drawing the eye to a
            // path's important endpoint. Risk still surfaces via the flag
            // badge, sanction ambient (§9d), and variant shapes.
            selector: `node[canvas_intent = "${CanvasNodeIntent.DANGER}"]`,
            style: {
                'width': 175,
                'height': 60,
                'border-width': 4,
                'border-color': '#991b1b', // Red-800
                'border-style': 'solid',
                'z-index': 1100,
                'font-weight': '800',
            }
        },
        // ── Uniform :selected feedback ─────────────────────────────────────
        // Overlay bloom on nodes is gone — selected/grabbed lift via
        // background-blacken (negative = lighter fill on dark). Taxonomic
        // border colour is left alone so mixer-red / exchange-green / etc.
        // survive a click. Edge selection overlay lives on edge:selected.
        {
            selector: 'node:selected',
            style: {
                'background-blacken': isDark ? -0.22 : 0.08,
            }
        },
        {
            selector: 'node:grabbed',
            style: {
                'background-blacken': isDark ? -0.32 : 0.12,
            }
        },
        // ── Canvas Intent: Edges ──
        // §10 alignment: canvas_intent is a semantic layer for the canvas-
        // sync pipeline. Intent → §10 vocabulary mapping:
        //   PRIMARY_TRACE   → amber stroke; the auto-trace primary path. The
        //                     edge is also tagged edgeType="gold-path" by
        //                     canvasSync, but this rule sits later in the
        //                     stylesheet so it must paint amber itself —
        //                     otherwise its line-color clobbers the gold-path
        //                     rule (last-rule-wins in cytoscape).
        //   DANGER_TRACE    → grey stroke (sanctions/danger lives on the
        //                     destination NODE per §10.3, not the edge).
        //   SECONDARY_TRACE → lighter-amber dashed (additional auto-trace
        //                     paths beyond the primary; thinner so the
        //                     primary stays the eye magnet).
        //   LOGICAL_LINK    → pink-dashed (§10.3 inferred slot; logical
        //                     relationships have no on-chain hash).
        //   AMBIENT_NOISE   → opacity-only; §10.7 focus state.
        {
            selector: `edge[canvas_intent = "${CanvasEdgeIntent.PRIMARY_TRACE}"]`,
            style: {
                'width': 3,
                'line-color': '#f59e0b',
                'line-style': 'solid',
                'target-arrow-color': '#f59e0b',
                'z-index': 1000,
                'font-weight': '700',
                'font-size': 10,
                'label': (ele: any) => {
                    const d = ele.data();
                    const val = d.total_value || d.value || 0;
                    const tok = d.token_symbol || '';
                    const fmt = formatCurrency(val, tok);
                    return d.trace_hop ? `${d.trace_hop} · ${fmt}` : fmt;
                },
                'text-opacity': 1,
                'text-background-color': isDark ? '#1e293b' : '#f8fafc',
                'text-background-opacity': 0.92,
                'text-background-padding': '2px',
                'text-border-color': isDark ? '#334155' : '#cbd5e1',
                'text-border-width': 1,
                'text-border-opacity': 0.5,
                'opacity': 1,
            }
        },
        {
            selector: `edge[canvas_intent = "${CanvasEdgeIntent.DANGER_TRACE}"]`,
            style: {
                'width': 3.5,
                'line-color': '#94a3b8',
                'line-style': 'solid',
                'target-arrow-color': '#94a3b8',
                'z-index': 1100,
                'font-weight': '800',
                'font-size': 10,
                'label': (ele: any) => {
                    const d = ele.data();
                    const val = d.total_value || d.value || 0;
                    const tok = d.token_symbol || '';
                    const fmt = formatCurrency(val, tok);
                    return d.trace_hop ? `${d.trace_hop} · ${fmt}` : fmt;
                },
                'text-opacity': 1,
                'text-background-color': isDark ? '#1e293b' : '#f8fafc',
                'text-background-opacity': 0.92,
                'text-background-padding': '3px',
                'text-border-color': isDark ? '#334155' : '#cbd5e1',
                'text-border-width': 1,
                'opacity': 1,
            }
        },
        {
            selector: `edge[canvas_intent = "${CanvasEdgeIntent.SECONDARY_TRACE}"]`,
            style: {
                'width': 1.5,
                'line-color': '#fbbf24',
                'line-style': 'dashed',
                'target-arrow-color': '#fbbf24',
                'font-size': 9,
                'text-background-color': isDark ? '#1e293b' : '#f8fafc',
                'text-background-opacity': 0.8,
            }
        },
        {
            selector: `edge[canvas_intent = "${CanvasEdgeIntent.LOGICAL_LINK}"]`,
            style: {
                'width': 2,
                'line-color': '#ec4899',
                'line-style': 'dashed',
                'target-arrow-color': '#ec4899',
                'font-size': 9,
                'text-background-color': isDark ? '#3a0a28' : '#fce7f3',
                'text-background-opacity': 0.8,
                'opacity': 0.9,
            }
        },
        {
            selector: `edge[canvas_intent = "${CanvasEdgeIntent.AMBIENT_NOISE}"]`,
            style: {
                'width': 0.5,
                'text-opacity': 0,
            }
        },
        // === AUTO-INVESTIGATION CANVAS STYLES ===
        // Escalation: ESCALATE nodes (pulsing red border)
        {
            selector: 'node.escalate-node',
            style: {
                'border-color': '#dc2626',
                'border-width': 4,
                'border-style': 'solid',
            }
        },
        // Escalation: INVESTIGATE nodes (amber border)
        {
            selector: 'node.investigate-node',
            style: {
                'border-color': '#f59e0b',
                'border-width': 3,
                'border-style': 'solid'
            }
        },
        // High-risk highlighting: emphasized nodes
        {
            selector: 'node.high-risk-highlight',
            style: {
                'opacity': 1,
                'border-width': 3,
                'border-color': '#ef4444'
            }
        },
        // Gold Path: backward/forward trace paths.
        // §10.4: stroke (line-color, width, arrow colors) REMOVED — taxonomic
        // stroke + magnitude tier own those channels. Overlay halo + z-index
        // come from the canonical §10.4 selector earlier in the cascade. This
        // block only forces opacity:1 so a trace-path edge can never be hidden
        // by an interaction-state dim (§10.7 focus-state work will codify the
        // opacity rule properly).
        {
            selector: 'edge.gold-path',
            style: {
                'opacity': 1,
            }
        },
        // R-CANVAS-03: "Follow the Money" highlight (dimming now via _visibility)
        {
            selector: 'node.follow-money-highlight',
            style: {
                'border-width': 3,
                'border-color': '#f59e0b',
                'z-index': 1000,
            }
        },
        // R-CANVAS-10: Visual indicator for manually positioned (locked) nodes
        {
            selector: 'node.manually-positioned',
            style: {
                'border-style': 'double',
            }
        },
        // Live investigation: nodes/edges appearing during SSE stream
        {
            // Phase D: Opacity removed from these selectors — handled by _visibility data.
            // Only decorative styling (borders, colors, line-styles) remains on CSS classes.
            selector: 'node.discovering',
            style: {
                'border-width': 2,
                'border-color': '#3b82f6',
                'border-style': 'dashed',
            }
        },
        {
            // §10.11: SSE-arrival "discovering" is an algorithm-running state.
            // Stroke colour stripped — the dashed line-style alone signals
            // "freshly arrived, not yet settled," and the dashed-arrival
            // treatment composes cleanly on top of taxonomic stroke.
            selector: 'edge.discovering',
            style: {
                'line-style': 'dashed',
            }
        },
        {
            selector: 'node.pruned',
            style: {
                'border-color': '#6b7280',
            }
        },
        {
            selector: 'node.bfs-explored',
            style: {
                'border-width': 1.5,
                'border-color': '#94a3b8',
                'border-style': 'dashed',
            }
        },
        {
            selector: 'node.orphan-node',
            style: {
                'border-color': '#6b7280',
                'border-style': 'dotted',
            }
        },
        {
            selector: 'node.top-counterparty',
            style: {
                'border-width': 2,
                'border-color': '#3b82f6',
            }
        },
        // §10.11: edge-orphan / edge-pruned / edge-dimmed are pure algorithm-
        // classification interaction states. Stroke colour overrides stripped —
        // these now carry NO style of their own (legacy classes survive for
        // call-site compatibility). Opacity is the proper channel for "this
        // edge is dimmed by the algorithm" and is driven by R11 `_visibility`
        // on the same elements via the existing opacity mapper.
        {
            selector: 'edge.edge-orphan',
            style: {}
        },
        {
            selector: 'edge.edge-pruned',
            style: {}
        },
        {
            selector: 'edge.edge-dimmed',
            style: {}
        },
    ], [isDark]);

    // Re-apply stylesheet when theme toggles (CytoscapeComponent only reads stylesheet on mount).
    useEffect(() => {
        const cy = cyRef.current;
        if (cy && !(cy as any).destroyed?.()) {
            cy.style().fromJson(style as cytoscape.StylesheetJson).update();
        }
    }, [style]);

    // Bind event listeners ONCE using stable refs — never re-bind on prop changes
    const listenersBoundRef = useRef(false);
    // Dedup native contextmenu hit-test vs Cytoscape cxttap (both can fire).
    const lastContextMenuOpenAtRef = useRef(0);
    const bindListeners = useCallback((cy: cytoscape.Core) => {
        if (listenersBoundRef.current) return;
        listenersBoundRef.current = true;

        const openNodeContextMenu = (ele: any, clientX: number, clientY: number) => {
            if (!ele || !onNodeContextMenuRef.current) return;
            if (shouldIgnoreDuplicateContextMenu(lastContextMenuOpenAtRef.current, Date.now())) return;
            const connectionCount = ele.neighborhood?.('node')?.length ?? 0;
            lastContextMenuOpenAtRef.current = Date.now();
            onNodeContextMenuRef.current(
                { ...ele.data(), id: ele.id(), connectionCount },
                { x: clientX, y: clientY },
            );
        };

        const openEdgeContextMenu = (ele: any, clientX: number, clientY: number) => {
            if (!ele || !onEdgeContextMenuRef.current) return;
            if (shouldIgnoreDuplicateContextMenu(lastContextMenuOpenAtRef.current, Date.now())) return;
            lastContextMenuOpenAtRef.current = Date.now();
            onEdgeContextMenuRef.current(
                { ...ele.data(), id: ele.id() },
                { x: clientX, y: clientY },
            );
        };

        // All handlers read from refs so they always use the latest callback
        const handleTap = (evt: any) => {
            const node = evt.target;
            // Step 4 (CANVAS_PLACEMENT_LANGUAGE §3.4) — synthetic-cluster
            // bubble: tap expands it back into its constituent leaves
            // instead of opening the Node Inspector.
            if (node.hasClass('synthetic-cluster')) {
                expandCluster(node.cy(), node.id());
                return;
            }
            // Clear single-edge selection when focus moves to a node
            node.cy().edges().removeClass('edge-single-selected');
            onNodeClickRef.current?.(node.data());
            // On click, also set hover data + bbox so flanking buttons remain visible
            if (onNodeHoverRef.current) {
                const renderedPos = node.renderedPosition();
                const containerRect = cy.container()?.getBoundingClientRect();
                if (containerRect) {
                    const connectionCount = node.neighborhood('node').length;
                    const nodeData = { ...node.data(), id: node.id(), connectionCount };
                    const renderedBBox = node.renderedBoundingBox();
                    onNodeHoverRef.current(nodeData, {
                        x: containerRect.left + renderedPos.x,
                        y: containerRect.top + renderedPos.y,
                        bbox: renderedBBox ? {
                            x1: containerRect.left + renderedBBox.x1,
                            y1: containerRect.top + renderedBBox.y1,
                            x2: containerRect.left + renderedBBox.x2,
                            y2: containerRect.top + renderedBBox.y2,
                        } : undefined,
                    });
                }
            }
        };

        const handleEdgeTap = (evt: any) => {
            const edge = evt.target;
            const originalEvent = evt.originalEvent as MouseEvent | undefined;
            const isMultiSelect = !!(originalEvent && (originalEvent.ctrlKey || originalEvent.metaKey));

            if (isMultiSelect) {
                const edgeId = edge.id();
                setMultiSelectedEdgeIds(prev => {
                    const next = new Set(prev);
                    if (next.has(edgeId)) {
                        next.delete(edgeId);
                    } else {
                        next.add(edgeId);
                    }
                    const nextIds = Array.from(next);
                    onEdgeSelectionChangeRef.current?.(nextIds);
                    return nextIds;
                });
                return;
            }

            setMultiSelectedEdgeIds(prev => {
                if (prev.length > 0) {
                    onEdgeSelectionChangeRef.current?.([]);
                    return [];
                }
                return prev;
            });

            // Visually mark the single clicked edge so the user sees what's selected
            // (prior behavior: Transfers tab updated silently, no canvas feedback).
            const cy = edge.cy();
            cy.edges().removeClass('edge-single-selected');
            edge.addClass('edge-single-selected');

            onEdgeClickRef.current?.(edge.data());
        };

        // Hover events for tooltip + flanking expand buttons
        const handleMouseOver = (evt: any) => {
            const node = evt.target;
            const cy = node.cy();

            // R-CANVAS-INTENT: Elevate hovered node and its neighbors without dimming others
            cy.batch(() => {
                node.addClass('hover-emphasis');
                node.neighborhood().addClass('hover-emphasis');
            });

            if (onNodeHoverRef.current) {
                const renderedPos = node.renderedPosition();
                const containerRect = cy.container()?.getBoundingClientRect();
                if (containerRect) {
                    const connectionCount = node.neighborhood('node').length;
                    const nodeData = { ...node.data(), id: node.id(), connectionCount };
                    // Include rendered bounding box so parent can position flanking buttons
                    const renderedBBox = node.renderedBoundingBox();
                    onNodeHoverRef.current(nodeData, {
                        x: containerRect.left + renderedPos.x,
                        y: containerRect.top + renderedPos.y,
                        bbox: renderedBBox ? {
                            x1: containerRect.left + renderedBBox.x1,
                            y1: containerRect.top + renderedBBox.y1,
                            x2: containerRect.left + renderedBBox.x2,
                            y2: containerRect.top + renderedBBox.y2,
                        } : undefined,
                    });
                }
            }
        };

        const handleMouseOut = (evt: any) => {
            const node = evt.target;
            const cy = node.cy();

            // R-CANVAS-INTENT: Revert temporary focus mode
            cy.batch(() => {
                cy.elements().removeClass('hover-emphasis');
            });

            if (onNodeHoverRef.current) {
                if (selectedNodeIdRef.current && node?.data?.()?.id === selectedNodeIdRef.current) return;
                onNodeHoverRef.current(null, { x: 0, y: 0 });
            }
        };

        // cxttap is primary: Cytoscape already resolved the target. Backdrop no
        // longer closes on the following contextmenu (NodeContextMenu fix), so
        // opening here is safe. Native hit-test below is backup + OS suppress.
        const clientXY = (evt: any, ele: any): { x: number; y: number } | null => {
            const oe = evt.originalEvent as MouseEvent | undefined;
            if (typeof oe?.clientX === 'number' && typeof oe?.clientY === 'number') {
                return { x: oe.clientX, y: oe.clientY };
            }
            const renderedPos = ele.renderedPosition?.();
            const rect = cy.container()?.getBoundingClientRect();
            if (!renderedPos || !rect) return null;
            return { x: rect.left + renderedPos.x, y: rect.top + renderedPos.y };
        };

        const handleContextMenu = (evt: any) => {
            const oe = evt.originalEvent as MouseEvent | undefined;
            oe?.preventDefault?.();
            const node = evt.target;
            const xy = clientXY(evt, node);
            if (xy) openNodeContextMenu(node, xy.x, xy.y);
        };

        const handleEdgeContextMenu = (evt: any) => {
            const oe = evt.originalEvent as MouseEvent | undefined;
            oe?.preventDefault?.();
            const edge = evt.target;
            const xy = clientXY(evt, edge);
            if (xy) openEdgeContextMenu(edge, xy.x, xy.y);
        };

        // Focus mode: Dim unrelated nodes when a node is selected
        // R-CANVAS-12: Supports multi-node selection (box-select) — unions all neighborhoods
        const handleSelect = () => {
            const selectedNodes = cy.nodes(':selected');
            if (onNodeSelectionChangeRef.current) {
                const selectedLocally = selectedNodes.map((n: any) => n.id());
                onNodeSelectionChangeRef.current(selectedLocally);
            }
        };

        const handleUnselect = () => {
            if (onNodeSelectionChangeRef.current) {
                const selectedLocally = cy.nodes(':selected').map((n: any) => n.id());
                onNodeSelectionChangeRef.current(selectedLocally);
            }
        };

        // Click on background unselects
        const handleBackgroundTap = (evt: any) => {
            if (evt.target === cy) {
                cy.elements().unselect();
                cy.edges().removeClass('edge-single-selected');
                if (onNodeSelectionChangeRef.current) {
                    onNodeSelectionChangeRef.current([]);
                }
                setMultiSelectedEdgeIds(prev => {
                    if (prev.length > 0) {
                        onEdgeSelectionChangeRef.current?.([]);
                        return [];
                    }
                    return prev;
                });
            }
        };

        // R-CANVAS-10: Track manually positioned nodes (preserves position during layout runs)
        const handleDragFree = (evt: any) => {
            const node = evt.target;
            const nodeId = node.id();
            manuallyPositionedNodeIdsRef.current.add(nodeId);
            node.addClass('manually-positioned');
            // Note: node is NOT locked here — it stays draggable.
            // Layout effect temporarily locks manual nodes during layout runs only.
        };

        // Notify parent on drag/pan/zoom so it can re-query bbox for pinned hover buttons
        const handleNodePositionChange = () => { onViewportChangeRef.current?.(); };
        const handleViewport = () => { onViewportChangeRef.current?.(); };

        // §10.11 edge hover. Cytoscape has no `:hover` pseudo-selector for
        // edges, so we drive the `.edge-hover` class via JS. Style is overlay-
        // only per the spec — taxonomic stroke is preserved.
        const handleEdgeMouseOver = (evt: any) => { evt.target.addClass('edge-hover'); };
        const handleEdgeMouseOut  = (evt: any) => { evt.target.removeClass('edge-hover'); };

        cy.on('tap', 'node', handleTap);
        cy.on('tap', 'edge', handleEdgeTap);
        cy.on('mouseover', 'node', handleMouseOver);
        cy.on('mouseout', 'node', handleMouseOut);
        cy.on('mouseover', 'edge', handleEdgeMouseOver);
        cy.on('mouseout', 'edge', handleEdgeMouseOut);
        cy.on('cxttap', 'node', handleContextMenu);
        cy.on('cxttap', 'edge', handleEdgeContextMenu);
        cy.on('select', 'node', handleSelect);
        cy.on('unselect', 'node', handleUnselect);
        cy.on('tap', handleBackgroundTap);
        cy.on('dragfree', 'node', handleDragFree);
        cy.on('drag', 'node', handleNodePositionChange);
        cy.on('viewport', handleViewport);
    }, []);

    // Suppress OS menu on canvas; open via hit-test as backup to cxttap.
    useEffect(() => {
        const resolveNearElement = (cy: cytoscape.Core, clientX: number, clientY: number) => {
            const renderer = (cy as any).renderer?.();
            if (!renderer?.projectIntoViewport || !renderer?.findNearestElement) return null;
            const pos = renderer.projectIntoViewport(clientX, clientY);
            return (
                renderer.findNearestElement(pos[0], pos[1], true, false) ||
                renderer.findNearestElement(pos[0], pos[1], false, false)
            );
        };

        const elementNearPointer = (ele: any, clientX: number, clientY: number, pad = 48) => {
            try {
                const container = ele.cy?.()?.container?.() ?? cyRef.current?.container();
                const rect = container?.getBoundingClientRect();
                if (!rect) return false;
                const bb = ele.renderedBoundingBox?.();
                if (!bb) return false;
                const lx = clientX - rect.left;
                const ly = clientY - rect.top;
                return lx >= bb.x1 - pad && lx <= bb.x2 + pad && ly >= bb.y1 - pad && ly <= bb.y2 + pad;
            } catch {
                return false;
            }
        };

        const onNativeContextMenu = (e: MouseEvent) => {
            const cy = cyRef.current;
            if (!cy || (cy as any).destroyed?.()) return;
            const container = cy.container();
            if (!container) return;
            const target = e.target;
            if (!(target instanceof Node)) return;

            const el = target instanceof Element ? target : target.parentElement;
            if (el?.closest?.('[data-testid="node-context-menu"], [data-testid="node-context-menu-backdrop"], [data-testid="edge-context-menu"], [data-testid="edge-context-menu-backdrop"]')) {
                e.preventDefault();
                return;
            }

            // Hover ←→ buttons sit outside the Cytoscape container.
            const hoverBtn = el?.closest?.('[data-node-hover-action]');
            if (hoverBtn) {
                e.preventDefault();
                const id = hoverBtn.getAttribute('data-node-id');
                if (!id) return;
                const ele = cy.getElementById(id);
                if (!ele?.length) return;
                if (shouldIgnoreDuplicateContextMenu(lastContextMenuOpenAtRef.current, Date.now())) return;
                const connectionCount = ele.neighborhood('node').length;
                lastContextMenuOpenAtRef.current = Date.now();
                onNodeContextMenuRef.current?.(
                    { ...ele.data(), id: ele.id(), connectionCount },
                    { x: e.clientX, y: e.clientY },
                );
                return;
            }

            if (!(container === target || container.contains(target))) {
                return;
            }

            e.preventDefault();
            if (shouldIgnoreDuplicateContextMenu(lastContextMenuOpenAtRef.current, Date.now())) return;

            try {
                let near = resolveNearElement(cy, e.clientX, e.clientY);
                let hit = classifyCanvasHit(near);

                if (hit === 'empty' && selectedNodeIdRef.current) {
                    const selected = cy.getElementById(selectedNodeIdRef.current);
                    if (selected?.length && elementNearPointer(selected, e.clientX, e.clientY)) {
                        near = selected;
                        hit = 'node';
                    }
                }

                if (hit === 'empty') return;

                lastContextMenuOpenAtRef.current = Date.now();
                if (hit === 'node' && onNodeContextMenuRef.current && near) {
                    const connectionCount = near.neighborhood('node').length;
                    onNodeContextMenuRef.current(
                        { ...near.data(), id: near.id(), connectionCount },
                        { x: e.clientX, y: e.clientY },
                    );
                } else if (hit === 'edge' && onEdgeContextMenuRef.current && near) {
                    onEdgeContextMenuRef.current(
                        { ...near.data(), id: near.id() },
                        { x: e.clientX, y: e.clientY },
                    );
                }
            } catch (err) {
                console.warn('[GraphCanvas] native contextmenu hit-test failed', err);
            }
        };

        document.addEventListener('contextmenu', onNativeContextMenu, true);
        return () => document.removeEventListener('contextmenu', onNativeContextMenu, true);
    }, []);

    useEffect(() => {
        const cy = cyRef.current;
        if (!cy) return;

        cy.edges().removeClass('edge-multi-selected');
        multiSelectedEdgeIds.forEach((edgeId) => {
            const edge = cy.getElementById(edgeId);
            if (edge) {
                edge.addClass('edge-multi-selected');
            }
        });
    }, [multiSelectedEdgeIds]);

    // Bidirectional sync: Inspector hover → canvas emphasis
    // Uses Zustand external subscription (no prop change → no R2 violation)
    useEffect(() => {
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;

        let prevId: string | null = null;
        let prevSource: string | null = null;
        const unsub = useSelectionStore.subscribe((state) => {
            const id = state.hoveredNodeId;
            const source = state.hoveredSource;
            if (id === prevId && source === prevSource) return;
            prevId = id;
            prevSource = source;
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const cy = cyRef.current;
                if (!cy) return;
                cy.batch(() => {
                    cy.elements().removeClass('hover-emphasis hover-deemphasis');
                    clearTransient(cy.elements(), 'hover');
                    if (id && source === 'inspector') {
                        const node = cy.getElementById(id);
                        if (node.length > 0) {
                            const neighborhood = node.neighborhood().union(node);
                            neighborhood.addClass('hover-emphasis');
                            const others = cy.elements().not(neighborhood);
                            others.addClass('hover-deemphasis');
                            dimTransient(others, VISIBILITY.PRUNED, 'hover');
                        }
                    }
                });
            }, 30);
        });

        return () => {
            unsub();
            if (debounceTimer) clearTimeout(debounceTimer);
        };
    }, []);

    // Track previous element count to distinguish "new data loaded" from "same data re-render"
    const prevElementCountRef = useRef(0);
    const prevNodeIdsRef = useRef<Set<string>>(new Set());
    const layoutRunningRef = useRef(false);
    const layoutSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // B3 — expansion-layout throttle state. `lastLayoutStartRef` records when the
    // last animated layout STARTED (performance.now); `trailingLayoutTimerRef` is
    // the single deferred "flush" armed when an expansion is throttled; bumping
    // `layoutFlushTick` re-enters the auto-layout effect when that flush fires.
    const lastLayoutStartRef = useRef(0);
    const trailingLayoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [layoutFlushTick, setLayoutFlushTick] = useState(0);
    // R24 stability: while an investigation stream holds layout, expansions are
    // deferred; a release (step boundary, complete, user action) bumps this tick
    // and re-enters the layout effect so ONE layout covers everything buffered.
    const layoutReleaseTick = usePendingLayoutStore((s) => s.releaseTick);

    // Last user-chosen layout mode. Written by `rearrangeLayout()` whenever
    // the user clicks Smart / Flow / Organic / Radial; read by the
    // auto-layout useEffect so subsequent expansions (SSE node arrivals,
    // expand-tx, autoTrace appending, etc.) honour the user's choice
    // instead of silently reverting to the default `flow` algorithm.
    //
    // Bug this fixes: prior to 2026-05-07, after a user clicked
    // Rearrange→Organic, any subsequent layout-firing event (e.g. an SSE
    // node_discovered firing while Holistic was still streaming, or an
    // eye-toggle that coincided with a delayed enrichment) would re-run
    // the auto-layout effect at line 2921 with `getLayoutConfig()` —
    // which defaults to `'flow'`. The organic stress positions were
    // wiped on every such event, manifesting as "going back to default
    // on every eye click" in the user's words. See
    // tests/eye-toggle-relayout.spec.ts TC-EYE-002 for the repro.
    const lastUserLayoutModeRef = useRef<RearrangeMode>('flow');

    const currencyMode = useFilterStore((s: FilterState) => s.currencyMode);

    // Currency-mode toggle propagation. The edge-label mappers in the
    // stylesheet read `useFilterStore.getState().currencyMode` at evaluation
    // time, but Cytoscape doesn't re-evaluate label mappers just because
    // a React state changed — only on element add/remove or explicit style
    // invalidation. Without this, switching ORIG ↔ USD would silently update
    // the Inspector (which is React-rendered) but leave canvas labels
    // stale until the next data load (expand, SSE event, fetch). Force a
    // restyle here so the canvas tracks the toggle live.
    useEffect(() => {
        const cy = cyRef.current;
        if (!cy) return;
        cy.style().update();
    }, [currencyMode]);

    useEffect(() => {
        const cy = cyRef.current;
        if (!cy || elements.length === 0) return;

        // Build current node ID set from elements prop
        const currentNodeIds = new Set<string>();
        elements.forEach(el => {
            if (!el.data?.source) { // nodes only (no source = not an edge)
                currentNodeIds.add(el.data.id);
            }
        });

        // Check if actual node composition changed (not just data updates / enrichment)
        const prevIds = prevNodeIdsRef.current;
        const nodesAdded = [...currentNodeIds].some(id => !prevIds.has(id));
        const nodesRemoved = [...prevIds].some(id => !currentNodeIds.has(id));
        const nodeSetChanged = nodesAdded || nodesRemoved;

        // Only re-run layout when nodes are truly added/removed.
        // Data-only updates (enrichment, labels, risk scores) skip layout entirely.
        if (nodeSetChanged) {
            const prevCount = prevIds.size;
            const addedCount = [...currentNodeIds].filter(id => !prevIds.has(id)).length;
            const removedCount = [...prevIds].filter(id => !currentNodeIds.has(id)).length;

            const isInitialLoad = prevCount === 0 && nodesAdded;
            const shouldSkipLayout = !isInitialLoad && !nodesAdded && nodesRemoved;
            
            if (shouldSkipLayout) {
                usePendingLayoutStore.getState().bumpRemoved(removedCount);
                prevElementCountRef.current = elements.length;
                prevNodeIdsRef.current = currentNodeIds;
                const manualIds = manuallyPositionedNodeIdsRef.current;
                if (manualIds.size > 0) {
                    for (const id of manualIds) if (!currentNodeIds.has(id)) manualIds.delete(id);
                }
                return;
            }

            // Expansion = new nodes added to an existing graph (not initial load).
            // ELK handles initial layout and expansion equally well; the per-hop
            // manual positioning passes (Pass 0–3) that used to live here have
            // been removed in favour of letting ELK do its job — for the common
            // fan-in / fan-out cases ELK's layered algorithm is what actually
            // separates clusters cleanly. The manual passes were only ever a
            // pre-positioning hint.
            const isExpansion = nodesAdded && prevIds.size > 0;

            // B3 — throttle expansion relayouts so a streaming investigation
            // doesn't "slot-machine" the canvas. If this expansion lands within
            // LAYOUT_EXPANSION_THROTTLE_MS of the last layout START, defer it:
            // we deliberately DO NOT advance the prev-id refs (so the next pass
            // still sees these nodes as "added" → nodeSetChanged stays true) and
            // arm a single trailing flush. The flush — or any later re-render that
            // crosses the throttle window first — then runs the existing layout
            // block ONCE against the fully-accumulated graph. Initial load
            // (prevIds empty) and pure removals are never throttled. Self-clearing
            // like layoutSettleTimerRef: not cancelled in the effect cleanup, so a
            // benign data-only re-render can't drop a pending flush.
            const layoutDecision = decideExpansionLayout({
                isExpansion,
                streamHold: usePendingLayoutStore.getState().streamHold
                    && !usePendingLayoutStore.getState().releaseGranted,
                msSinceLastLayout: performance.now() - lastLayoutStartRef.current,
            });
            if (layoutDecision === 'hold') {
                // Streaming hold: defer WITHOUT advancing the prev-id refs, so the
                // release re-enters with every buffered node still "added" and
                // lays them all out once (the throttle still applies then).
                return;
            }
            if (layoutDecision === 'run' && usePendingLayoutStore.getState().releaseGranted) {
                usePendingLayoutStore.getState().consumeRelease(); // this layout is the released one
            }
            if (layoutDecision === 'throttle') {
                const sinceLastLayout = performance.now() - lastLayoutStartRef.current;
                if (sinceLastLayout < LAYOUT_EXPANSION_THROTTLE_MS) {
                    if (trailingLayoutTimerRef.current) clearTimeout(trailingLayoutTimerRef.current);
                    trailingLayoutTimerRef.current = setTimeout(() => {
                        trailingLayoutTimerRef.current = null;
                        setLayoutFlushTick(t => t + 1);
                    }, LAYOUT_EXPANSION_THROTTLE_MS - sinceLastLayout + 40);
                    return; // defer — prev-id refs intentionally left unchanged
                }
            }

            // Preserve user's viewport on expansion. Without this, every
            // expand/trace/SSE event re-fits and re-centers the graph,
            // yanking the user out of the area they're investigating.
            // Captured BEFORE layout runs so we can restore after settle.
            const preservedZoom = isExpansion ? cy.zoom() : null;
            const preservedPan = isExpansion ? { ...cy.pan() } : null;

            // (Dead code removed: a duplicate copy of the expansion Pass 0–3
            // block used to live here from a bad merge in the stack-overflow
            // fix. It re-ran the passes with fresh jitter, overwriting any
            // fan-in ring positioning applied above.)

            // Overlap guard — but don't starve expansions during rapid SSE/auto-invest
            // streaming. If the flag is stuck from a prior run whose settle timer is
            // still pending, pre-empt it so the newest batch actually gets laid out.
            if (layoutRunningRef.current) {
                if (layoutSettleTimerRef.current) {
                    clearTimeout(layoutSettleTimerRef.current);
                    layoutSettleTimerRef.current = null;
                }
            }
            layoutRunningRef.current = true;
            // B3 — stamp the throttle window from the moment a layout actually starts.
            lastLayoutStartRef.current = performance.now();

            // A layout is actually about to run — clear pending counters.
            usePendingLayoutStore.getState().clear();
            // (addedCount is tracked for future telemetry; currently unused)
            void addedCount;

            logLayoutEvent('layout_triggered', {
                isExpansion,
                prevNodeCount: prevIds.size,
                currentNodeCount: currentNodeIds.size,
                nodesAdded,
                nodesRemoved,
                manuallyLockedCount: manuallyPositionedNodeIdsRef.current.size,
            });

            // R-CANVAS-10: Prune manually-positioned IDs for nodes no longer in graph
            const manualIds = manuallyPositionedNodeIdsRef.current;
            if (nodesRemoved && manualIds.size > 0) {
                for (const id of manualIds) {
                    if (!currentNodeIds.has(id)) {
                        manualIds.delete(id);
                    }
                }
            }

            // Lock manually positioned nodes before layout so ELK respects pin state
            if (manualIds.size > 0) {
                cy.nodes().forEach((node: any) => {
                    if (manualIds.has(node.id())) {
                        node.lock();
                    }
                });
            }

            // NOTE: We do NOT lock pre-existing nodes during expansion.
            // ELK's layered algorithm needs full freedom to assign nodes to correct layers
            // based on edge direction (source→target). Locking the root node would force
            // all new nodes to one side, ignoring incoming vs outgoing direction.

            // Always run ELK layout — handles both initial load and expansion.
            // For expansions, we disable fit so layout doesn't fight viewport restore.
            //
            // Layout-mode selection: respect the user's last explicit choice
            // (Smart / Flow / Organic / Radial via the toolbar). Without this,
            // any node-add event reverts the canvas to layered LTR ('flow'),
            // wiping a prior Organic / Radial arrangement on every SSE node
            // arrival or programmatic expansion. Initial load keeps 'flow' as
            // the default because that's what the ref starts at; only after
            // the user has clicked a Rearrange button does the choice stick.
            // For 'smart', defer to analyzeGraph() so the algorithm picker
            // stays adaptive as the canvas grows.
            const userMode = lastUserLayoutModeRef.current;
            const resolvedMode: ResolvedLayoutMode = userMode === 'smart'
                ? analyzeGraph(cy.elements()).mode
                : userMode;
            const config = { ...getLayoutConfig(resolvedMode), fit: !isExpansion };
            const layoutInstance = cy.layout(config);
            try {
                layoutInstance.run();
            } catch (e) {
                console.error('[GraphCanvas] ELK layout failed in useEffect, falling back to cose', e);
                cy.layout({
                    name: 'cose',
                    animate: true,
                    animationDuration: 400,
                    fit: !isExpansion,
                    padding: 40,
                    randomize: false,
                } as any).run();
            }

            // Settle time for ELK is the animation duration + slight buffer
            const settleTime = 460;
            if (layoutSettleTimerRef.current) clearTimeout(layoutSettleTimerRef.current);
            layoutSettleTimerRef.current = setTimeout(() => {
                layoutSettleTimerRef.current = null;
                // Guard against use-after-destroy. These settle timers are
                // intentionally not cleared on effect cleanup (see the throttle
                // comment above), so they can fire ~460ms after react-cytoscapejs
                // has destroyed this instance on unmount/remount. After destroy
                // `_private.renderer` is null and any geometry read (boundingBox,
                // which calls cy.headless()) throws "Cannot read properties of
                // null (reading 'isHeadless')". Bail out if the core is gone.
                if (cy.destroyed()) return;
                // Unlock ALL nodes after layout settles (including manually positioned).
                // Manual nodes are only locked during layout runs to preserve their position.
                // They remain fully draggable between layout runs.
                cy.nodes().unlock();

                // Step 3 — within-column sub-axis. Only meaningful for 'flow'
                // (layered LTR), where ELK has assigned nodes to discrete
                // columns. Organic / radial position nodes by force, so there
                // are no columns to re-stack.
                if (resolvedMode === 'flow') {
                    const subAxis = applyWithinColumnSubAxis(cy);
                    if (subAxis.reordered > 0) {
                        logLayoutEvent('subaxis_applied', {
                            columnsReordered: subAxis.columns,
                            nodesReordered: subAxis.reordered,
                        });
                    }
                }

                // Step 4 — context-leaf clustering. Past CLUSTER_AUTO_THRESHOLD_NODES
                // (80) the canvas can't hold every leaf at a readable zoom (§3.4).
                // Fold hubs with ≥10 unknown context leaves into synthetic-cluster
                // bubbles. Click on a bubble expands it back. Idempotent.
                if (cy.nodes().length >= CLUSTER_AUTO_THRESHOLD_NODES) {
                    const cluster = clusterContextLeaves(cy);
                    if (cluster.collapsed > 0) {
                        logLayoutEvent('clusters_collapsed', {
                            hubsCollapsed: cluster.candidates.length,
                            leavesFolded: cluster.collapsed,
                        });
                    }
                }

                // ── Canvas Debug: log settled positions ──
                logLayoutEvent('layout_settled', {
                    isExpansion,
                    nodeCount: cy.nodes().length,
                    edgeCount: cy.edges().length,
                    nodePositions: cy.nodes().map((n: any) => ({
                        id: n.id().slice(0, 12),
                        x: Math.round(n.position().x),
                        y: Math.round(n.position().y),
                        entity: n.data().entity_name || '',
                    })),
                });

                // ── CANVAS AUDIT LOG ──────────────────────────────────────────────────
                // Full dump of every node and edge after layout settles.
                // Remove or gate behind a flag once overlap issues are resolved.
                console.groupCollapsed(
                    `%c[CANVAS AUDIT] layout=elk nodes=${cy.nodes().length} edges=${cy.edges().length} expansion=${isExpansion}`,
                    'color:#6366f1;font-weight:bold'
                );

                // ── NODES ──
                console.group('%cNODES', 'color:#22c55e;font-weight:bold');
                const nodeRows: any[] = [];
                cy.nodes().forEach((n: any) => {
                    const pos = n.position();
                    const bb = n.boundingBox();
                    const d = n.data();
                    nodeRows.push({
                        id: n.id(),
                        entity: d.entity_name || '',
                        type: d.entity_type || d.type || '',
                        risk: d.risk_level || (d.risk_score != null ? d.risk_score.toFixed(2) : ''),
                        x: Math.round(pos.x),
                        y: Math.round(pos.y),
                        w: Math.round(bb.x2 - bb.x1),
                        h: Math.round(bb.y2 - bb.y1),
                        'bb.x1': Math.round(bb.x1),
                        'bb.y1': Math.round(bb.y1),
                        'bb.x2': Math.round(bb.x2),
                        'bb.y2': Math.round(bb.y2),
                        locked: n.locked(),
                        classes: n.classes().join(' ') || '',
                    });
                });
                console.table(nodeRows);
                console.groupEnd();

                // ── EDGES ──
                console.group('%cEDGES', 'color:#f59e0b;font-weight:bold');
                const edgeRows: any[] = [];
                cy.edges().forEach((e: any) => {
                    const d = e.data();
                    let midX = '?', midY = '?';
                    try {
                        const mid = e.renderedMidpoint ? e.renderedMidpoint() : null;
                        if (mid) { midX = Math.round(mid.x).toString(); midY = Math.round(mid.y).toString(); }
                    } catch (_) { /* not rendered yet */ }
                    edgeRows.push({
                        id: e.id(),
                        source: (d.source || '').slice(0, 16),
                        target: (d.target || '').slice(0, 16),
                        edgeType: d.edgeType || '',
                        value: d.value ?? d.total_value ?? '',
                        token: d.token_symbol || '',
                        tx_count: d.tx_count || 1,
                        consolidated: !!d.is_consolidated,
                        'mid.x': midX,
                        'mid.y': midY,
                        classes: e.classes().join(' ') || '',
                    });
                });
                console.table(edgeRows);
                console.groupEnd();

                // ── OVERLAP DETECTION ──
                console.group('%cOVERLAP CHECK', 'color:#ef4444;font-weight:bold');
                const nodeBBs: Array<{ id: string; x1: number; y1: number; x2: number; y2: number }> = [];
                cy.nodes().forEach((n: any) => {
                    const bb = n.boundingBox();
                    nodeBBs.push({ id: n.id(), x1: bb.x1, y1: bb.y1, x2: bb.x2, y2: bb.y2 });
                });
                let overlapCount = 0;
                for (let i = 0; i < nodeBBs.length; i++) {
                    for (let j = i + 1; j < nodeBBs.length; j++) {
                        const a = nodeBBs[i], b = nodeBBs[j];
                        const overlapX = a.x1 < b.x2 && a.x2 > b.x1;
                        const overlapY = a.y1 < b.y2 && a.y2 > b.y1;
                        if (overlapX && overlapY) {
                            const gapX = Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1);
                            const gapY = Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1);
                            console.warn(
                                `OVERLAP: "${a.id.slice(0,14)}" ↔ "${b.id.slice(0,14)}"  ` +
                                `intersect=${Math.round(gapX)}×${Math.round(gapY)}px`
                            );
                            overlapCount++;
                        }
                    }
                }
                if (overlapCount === 0) {
                    console.log('%c✓ No overlapping nodes', 'color:#22c55e');
                } else {
                    console.error(`✗ ${overlapCount} overlap pair(s) detected`);
                }
                console.groupEnd();

                console.groupEnd(); // end CANVAS AUDIT

                // Zoom strategy: fit the IMPORTANT elements, not everything.
                // Priority: gold-path > emphasized > selected neighborhood > all visible.
                // This prevents context nodes (dimmed, background) from forcing a zoom-out.
                const MAX_ZOOM = 1.75;
                const MIN_ZOOM = 0.5;
                const currentSelId = selectedNodeIdRef.current;
                const sel = currentSelId ? cy.getElementById(currentSelId) : null;

                // Determine the focus set — what the user cares about
                const goldPathEls = cy.elements('.gold-path, [edgeType="gold-path"]');
                const goldNodes = goldPathEls.connectedNodes();
                const goldAll = goldPathEls.union(goldNodes);

                const emphasizedEls = cy.elements('.edge-emphasized');
                const emphasizedNodes = emphasizedEls.connectedNodes();
                const emphasizedAll = emphasizedEls.union(emphasizedNodes);

                const visibleEls = cy.elements().filter((ele: any) => {
                    const v = ele.data('_visibility');
                    return v === undefined || v === null || v >= 1.0;
                });

                // CANVAS_PLACEMENT_LANGUAGE §8.5: prefer declared subjects
                // (the investigation root) over arbitrary selection so that
                // the initial viewport always lands on the primary object.
                // Falls through to selection / visible / all when no node
                // declares placement_role='subject' yet.
                const subjectNodes = cy.nodes().filter(
                    (n: any) => n.data('placement_role') === 'subject'
                );

                // Pick the best focus set
                let focusSet;
                if (goldAll.length > 0) {
                    focusSet = goldAll;
                } else if (emphasizedAll.length > 0) {
                    focusSet = emphasizedAll;
                } else if (subjectNodes.length > 0) {
                    // Declared subject(s) + 1-hop neighbourhood — keeps the
                    // primary objects in view even when nothing is selected.
                    focusSet = subjectNodes.neighborhood().union(subjectNodes);
                } else if (sel && sel.length > 0) {
                    // Selected node + its immediate neighborhood
                    focusSet = sel.neighborhood().union(sel);
                } else if (visibleEls.length > 0 && visibleEls.length < cy.elements().length) {
                    // Some elements are filtered — fit only visible ones
                    focusSet = visibleEls;
                } else {
                    focusSet = undefined; // all elements (default)
                }

                // Viewport policy:
                //   - Initial load: fit & center on the focus set (gold path,
                //     emphasized, selected neighborhood, or all visible).
                //   - Small expansion (<=10 new nodes): restore pre-layout
                //     viewport so we don't yank the user out of the area
                //     they're investigating.
                //   - Large expansion (>10 new nodes): fit to include the new
                //     nodes. Without this, a fan-in of 80 counterparties at
                //     ring radius ~500 is entirely off-screen from the root's
                //     original zoom level, and the user sees "nothing happened".
                const newCount = [...currentNodeIds].filter(id => !prevIds.has(id)).length;
                const largeExpansion = isExpansion && newCount > 10;
                if (isExpansion && !largeExpansion && preservedZoom !== null && preservedPan !== null) {
                    cy.zoom(preservedZoom);
                    cy.pan(preservedPan);
                } else {
                    cy.fit(focusSet, 50);
                    if (cy.zoom() > MAX_ZOOM) {
                        cy.zoom(MAX_ZOOM);
                    } else if (cy.zoom() < MIN_ZOOM) {
                        cy.zoom(MIN_ZOOM);
                    }
                    if (focusSet && focusSet.length > 0) {
                        cy.center(focusSet);
                    } else if (sel && sel.length > 0) {
                        cy.center(sel);
                    } else {
                        cy.center();
                    }
                }
                layoutRunningRef.current = false;
            }, settleTime);

            prevElementCountRef.current = elements.length;
            prevNodeIdsRef.current = currentNodeIds;

            return () => {
                // Don't clear layoutSettleTimerRef here — data-only re-renders
                // (enrichment) would cancel the zoom logic before it fires.
                // The ref-based timer self-clears on next layout run instead.
                layoutRunningRef.current = false;
            };
        } else {
            // Data-only update: just update tracked refs, no layout run
            prevElementCountRef.current = elements.length;
            // A released layout with nothing buffered is spent: do not let it
            // leak into the next mid-step flush.
            if (usePendingLayoutStore.getState().releaseGranted) {
                usePendingLayoutStore.getState().consumeRelease();
            }
        }
    }, [elements, selectedNodeId, currencyMode, layoutFlushTick, layoutReleaseTick]);

    // Update zoom level display
    const updateZoomLevel = useCallback(() => {
        const cy = cyRef.current;
        if (cy) {
            setZoomLevel(Math.round(cy.zoom() * 100));
        }
    }, []);

    // Track zoom changes
    useEffect(() => {
        const cy = cyRef.current;
        if (!cy) return;
        cy.on('zoom', updateZoomLevel);
        return () => {
            cy.removeListener('zoom', updateZoomLevel);
        };
    }, [updateZoomLevel]);

    // Zoom controls
    const handleZoomIn = useCallback(() => {
        const cy = cyRef.current;
        if (cy) {
            const newLevel = Math.min(cy.zoom() * 1.3, 3); // Max 300%
            cy.zoom({
                level: newLevel,
                renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 }
            });
        }
    }, []);

    const handleZoomOut = useCallback(() => {
        const cy = cyRef.current;
        if (cy) {
            const newLevel = Math.max(cy.zoom() / 1.3, 0.1); // Min 10%
            cy.zoom({
                level: newLevel,
                renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 }
            });
        }
    }, []);

    const handleFitToView = useCallback(() => {
        const cy = cyRef.current;
        if (cy) {
            cy.fit(undefined, 40);
            cy.center();
        }
    }, []);

    const handleResetZoom = useCallback(() => {
        const cy = cyRef.current;
        if (cy) {
            cy.zoom(1);
            cy.center();
        }
    }, []);

    // P4: ResizeObserver — recalculate cy's internal dimensions when the
    // container resizes (grid column drag, panel docking, Inspector content
    // height changes on node selection). We deliberately do NOT call cy.fit()
    // here: that was fighting the user's zoom state. Any transient Inspector
    // height change when selecting a new node would yank the viewport back to
    // "fit all". cy.resize() alone keeps hit-testing correct without touching
    // the user's zoom/pan.
    const canvasContainerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = canvasContainerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => {
            const cy = cyRef.current;
            if (cy) cy.resize();
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Cytoscape can mount into a 0×0 box while the flex/absolute height chain
    // settles. Resize + fit once after the first node(s) appear so they paint.
    const prevElementsLenRef = useRef(0);
    useEffect(() => {
        const prevLen = prevElementsLenRef.current;
        prevElementsLenRef.current = elements.length;
        if (elements.length === 0) return;
        const isFirstPaint = prevLen === 0;
        const id = requestAnimationFrame(() => {
            const cy = cyRef.current;
            if (!cy || (cy as any).destroyed?.()) return;
            cy.resize();
            if (isFirstPaint && cy.nodes().length > 0) {
                cy.fit(undefined, 40);
            }
        });
        return () => cancelAnimationFrame(id);
    }, [elements.length]);

    return (
        <div
            ref={canvasContainerRef}
            className="absolute inset-0"
            // Suppress OS menu on zoom chrome / grid / empty padding — not only
            // on the Cytoscape container — so the browser menu never stacks
            // beside the in-app node/edge menus.
            onContextMenu={(e) => e.preventDefault()}
        >
            {/* z-0 keeps the opaque grid behind Cytoscape's canvases */}
            <CanvasGridBackground className="z-0" />

            <CytoscapeComponent
                elements={safeElements}
                style={{ width: '100%', height: '100%', background: 'transparent', position: 'relative', zIndex: 1 }}
                stylesheet={style}
                boxSelectionEnabled={true}
                minZoom={0.1}
                maxZoom={4}
                // Do NOT pass layout prop here to avoid double-run. We handle it in useEffect.
                // layout={layout}
                cy={(cy) => {
                    if (cyRef.current !== cy) {
                        cyRef.current = cy;
                        listenersBoundRef.current = false;
                        // Tame scroll-wheel zoom — default 1.0 is way too sensitive
                        // on trackpads and high-res scroll wheels (causes 0→400% in one scroll).
                        // 0.08 gives ~25% change per scroll tick.
                        (cy as any).wheelSensitivity = 0.08;

                        // Suppress Cytoscape's "invalid edge endpoints" warning that fires
                        // every animation frame when two connected nodes overlap. This is a
                        // known Cytoscape.js behaviour, not a bug — edges simply don't render
                        // until the nodes separate. The console spam is harmless but noisy.
                        const origWarn = console.warn;
                        console.warn = (...args: any[]) => {
                            if (typeof args[0] === 'string' && args[0].includes('invalid endpoints')) return;
                            origWarn.apply(console, args);
                        };

                        // NOTE: We deliberately do NOT install a global window 'error'
                        // swallow for Cytoscape "isHeadless"/"boundingBox" errors. The
                        // real source — deferred layout-settle timers reading geometry
                        // after the instance is destroyed — is fixed at the source via
                        // `cy.destroyed()` guards on those timers. A global preventDefault
                        // on any error containing those substrings would also hide genuine
                        // bugs, leaks a listener per cy instance, and never actually
                        // stopped Sentry from capturing the error. Fix new races at the
                        // source, not here.
                    }
                    bindListeners(cy);

                    // Expose Cytoscape instance for debugging, E2E testing, and CLAURST
                    // Read-only reference — no security risk, enables pipeline assertions
                    (window as any).__cy = cy;
                    // Expose Step-4 cluster expand for E2E (cytoscape's emit('tap')
                    // doesn't reliably bubble to delegated cy.on('tap','node',...))
                    (window as any).__expandCluster = (id: string) => expandCluster(cy, id);
                }}
            />

            <CanvasZoomControls
                zoomLevel={zoomLevel}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onFitToView={handleFitToView}
                onResetZoom={handleResetZoom}
            />
        </div>
    );
});

export const GraphCanvas = React.memo(GraphCanvasImpl, (prev, next) =>
    prev.elements === next.elements &&
    prev.selectedNodeId === next.selectedNodeId
);
